import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import api from '../services/api.jsx';
import RoleManagement from '../components/RoleManagement';
import ConfirmModal from '../components/ConfirmModal.jsx';
import AddUserModal from '../components/AddUserModal.jsx';
import ImportUsersModal from '../components/ImportUsersModal.jsx';
import ZohoJobDivaEtlMap from '../components/ZohoJobDivaEtlMap.jsx';
import PolicyReviewAdminTab from './PolicyReviewAdminTab.jsx';
import '../styles/Admin.css';

const Admin = () => {
  const { user, hasPermission, hasAnyPermission } = useAuth();
  const canManageUsers = hasAnyPermission(['admin:users:read', 'admin:users:write']);
  const canManagePermissions = hasPermission('admin:permissions:write');
  const canOperateEmailWorker = hasPermission('admin:ops:emailworker');
  const canAccessAdmin = canManageUsers || canManagePermissions || canOperateEmailWorker;
  const canRunJobDivaDiagnostics = canAccessAdmin;
  const canRunZohoDiagnostics = canAccessAdmin;
  const canRunLaborEdgeDiagnostics = canAccessAdmin;
  const canRunIndeedRssDiagnostics = canAccessAdmin;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleUpdates, setRoleUpdates] = useState({});
  const [activeTab, setActiveTab] = useState('users');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showImportUsersModal, setShowImportUsersModal] = useState(false);
  const [pendingStatusUser, setPendingStatusUser] = useState(null);
  const [userStatusError, setUserStatusError] = useState('');
  const [userStatusUpdatingId, setUserStatusUpdatingId] = useState(null);
  const [emailDiagnostics, setEmailDiagnostics] = useState([]);
  const [loadingEmailDiagnostics, setLoadingEmailDiagnostics] = useState(false);
  const [emailDiagnosticsError, setEmailDiagnosticsError] = useState('');
  const [rsvpAccessDiagnostics, setRsvpAccessDiagnostics] = useState([]);
  const [loadingRsvpAccessDiagnostics, setLoadingRsvpAccessDiagnostics] = useState(false);
  const [rsvpAccessDiagnosticsError, setRsvpAccessDiagnosticsError] = useState('');
  const [emailDiagnosticFilters, setEmailDiagnosticFilters] = useState({
    recipient: '',
    status: '',
    sender: '',
  });
  const [rsvpAccessDiagnosticFilters, setRsvpAccessDiagnosticFilters] = useState({
    outcome: '',
    action: '',
    eventId: '',
    likelyScanner: '',
    tokenFingerprint: '',
    recipientEmail: '',
  });
  const [emailWorkerStatus, setEmailWorkerStatus] = useState(null);
  const [loadingEmailWorkerStatus, setLoadingEmailWorkerStatus] = useState(false);
  const [killingEmailWorker, setKillingEmailWorker] = useState(false);
  const [startingEmailWorker, setStartingEmailWorker] = useState(false);
  const [clearingQueue, setClearingQueue] = useState(false);
  const [emailWorkerActionError, setEmailWorkerActionError] = useState('');
  const [emailWorkerActionSuccess, setEmailWorkerActionSuccess] = useState('');
  const [confirmAction, setConfirmAction] = useState({ show: false, type: null });
  const [jobDivaStatus, setJobDivaStatus] = useState(null);
  const [jobDivaStatusLoading, setJobDivaStatusLoading] = useState(false);
  const [jobDivaTestLoading, setJobDivaTestLoading] = useState(false);
  const [jobDivaCleanupLoading, setJobDivaCleanupLoading] = useState(false);
  const [jobDivaResetLoading, setJobDivaResetLoading] = useState(false);
  const [jobDivaResult, setJobDivaResult] = useState(null);
  const [jobDivaError, setJobDivaError] = useState('');
  const [jobDivaSubTab, setJobDivaSubTab] = useState('jobs');
  const [jobDivaCandidateId, setJobDivaCandidateId] = useState('');
  const [jobDivaCandidateResult, setJobDivaCandidateResult] = useState(null);
  const [jobDivaCandidateLoading, setJobDivaCandidateLoading] = useState(false);
  const [jobDivaCandidateError, setJobDivaCandidateError] = useState('');
  const [jobDivaUsers, setJobDivaUsers] = useState([]);
  const [jobDivaAssignableDivisions, setJobDivaAssignableDivisions] = useState([]);
  const [jobDivaUsersLoading, setJobDivaUsersLoading] = useState(false);
  const [jobDivaUsersError, setJobDivaUsersError] = useState('');
  const [jobDivaUsersSuccess, setJobDivaUsersSuccess] = useState('');
  const [jobDivaUserFilters, setJobDivaUserFilters] = useState({ search: '', division: '__none__', showInactive: false });
  const [jobDivaSelectedUserIds, setJobDivaSelectedUserIds] = useState(new Set());
  const [jobDivaBulkDivision, setJobDivaBulkDivision] = useState('');
  const [jobDivaRowDivisions, setJobDivaRowDivisions] = useState({});
  const [jobDivaUserSavingId, setJobDivaUserSavingId] = useState(null);
  const [jobDivaBulkRunning, setJobDivaBulkRunning] = useState(false);
  const [jobDivaBulkConfirmOpen, setJobDivaBulkConfirmOpen] = useState(false);
  const [jobDivaBulkResult, setJobDivaBulkResult] = useState(null);
  const [jobDivaPage, setJobDivaPage] = useState(1);
  const [jobDivaPageSize, setJobDivaPageSize] = useState(25);
  const [jobDivaFlaggedItems, setJobDivaFlaggedItems] = useState([]);
  const [jobDivaFlaggedLoading, setJobDivaFlaggedLoading] = useState(false);
  const [jobDivaFlaggedError, setJobDivaFlaggedError] = useState('');
  const [jobDivaFlaggedExpanded, setJobDivaFlaggedExpanded] = useState(new Set());
  const [jobDivaCleanupProgress, setJobDivaCleanupProgress] = useState(null);
  const [jobDivaNowMs, setJobDivaNowMs] = useState(Date.now());
  const [jobDivaReviewNotes, setJobDivaReviewNotes] = useState({});
  const [jobDivaReviewActionId, setJobDivaReviewActionId] = useState(null);
  const [selectedJobDivaPreviewJob, setSelectedJobDivaPreviewJob] = useState(null);
  const [selectedZohoPreviewJob, setSelectedZohoPreviewJob] = useState(null);
  const [jobDivaForm, setJobDivaForm] = useState({
    reference: '',
    keyword: '',
    descStatus: '',
    company: '',
    division: '',
    priority: '',
    posttoportal: '',
  });
  const [jobDivaFiltersAppliedAt, setJobDivaFiltersAppliedAt] = useState(null);
  const [jobDivaFiltersEnabled, setJobDivaFiltersEnabled] = useState(true);
  const [jobDivaSchedule, setJobDivaSchedule] = useState(null);
  const [jobDivaScheduleLoading, setJobDivaScheduleLoading] = useState(false);
  const [jobDivaScheduleSaving, setJobDivaScheduleSaving] = useState(false);
  const [jobDivaScheduleError, setJobDivaScheduleError] = useState('');
  const [jobDivaScheduleSuccess, setJobDivaScheduleSuccess] = useState('');
  const jobDivaInitialLoadRef = useRef(false);
  const [jobDivaScheduleForm, setJobDivaScheduleForm] = useState({
    intervalMinutes: 0,
    nightlyHourUtc: 2,
    nightlyMinuteUtc: 0,
    runOnStartup: true,
  });
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [roleFilters, setRoleFilters] = useState([]);
  const [openRoleFilterDropdown, setOpenRoleFilterDropdown] = useState(false);
  const [rsvpAccessRecipients, setRsvpAccessRecipients] = useState([]);
  const [rsvpRecipientSearchTerm, setRsvpRecipientSearchTerm] = useState('');
  const [showRsvpRecipientSuggestions, setShowRsvpRecipientSuggestions] = useState(false);
  const [zohoStatus, setZohoStatus] = useState(null);
  const [zohoStatusLoading, setZohoStatusLoading] = useState(false);
  const [zohoActionLoading, setZohoActionLoading] = useState(false);
  const [zohoMetadataLoading, setZohoMetadataLoading] = useState(false);
  const [zohoResult, setZohoResult] = useState(null);
  const [zohoMetadata, setZohoMetadata] = useState(null);
  const [zohoError, setZohoError] = useState('');
  const [zohoAuthorizeUrl, setZohoAuthorizeUrl] = useState('');
  const [zohoOAuthLoading, setZohoOAuthLoading] = useState(false);
  const [zohoAuthorizationCode, setZohoAuthorizationCode] = useState('');
  const [zohoRedirectUriInput, setZohoRedirectUriInput] = useState('');
  const [zohoPage, setZohoPage] = useState(1);
  const [zohoPageSize, setZohoPageSize] = useState(25);
  const [zohoMaintenanceStatus, setZohoMaintenanceStatus] = useState(null);
  const [zohoMaintenanceHistory, setZohoMaintenanceHistory] = useState([]);
  const [zohoMaintenanceLoading, setZohoMaintenanceLoading] = useState(false);
  const [zohoMaintenanceHistoryLoading, setZohoMaintenanceHistoryLoading] = useState(false);
  const [zohoMaintenanceHistoryError, setZohoMaintenanceHistoryError] = useState('');
  const [zohoMaintenanceActionLoading, setZohoMaintenanceActionLoading] = useState(false);
  const [zohoMaintenanceError, setZohoMaintenanceError] = useState('');
  const [zohoMaintenanceSuccess, setZohoMaintenanceSuccess] = useState('');
  const [zohoMaintenanceTicketSummary, setZohoMaintenanceTicketSummary] = useState(null);
  const [zohoMaintenanceTickets, setZohoMaintenanceTickets] = useState([]);
  const [zohoMaintenanceTicketStatus, setZohoMaintenanceTicketStatus] = useState('');
  const [zohoMaintenanceSubTab, setZohoMaintenanceSubTab] = useState('ticket-dashboard');
  const [zohoMaintenanceForm, setZohoMaintenanceForm] = useState({
    enabled: true,
    isPaused: false,
    runDayOfMonth: 1,
    runHourUtc: 3,
    runMinuteUtc: 0,
  });
  const [zohoForm, setZohoForm] = useState({
    keyword: '',
    location: '',
    department: '',
    publish: '',
  });
  const [zohoFiltersAppliedAt, setZohoFiltersAppliedAt] = useState(null);
  const [laborEdgeStatus, setLaborEdgeStatus] = useState(null);
  const [laborEdgeStatusLoading, setLaborEdgeStatusLoading] = useState(false);
  const [laborEdgeActionLoading, setLaborEdgeActionLoading] = useState(false);
  const [laborEdgeCleanupLoading, setLaborEdgeCleanupLoading] = useState(false);
  const [laborEdgeResetLoading, setLaborEdgeResetLoading] = useState(false);
  const [laborEdgeResult, setLaborEdgeResult] = useState(null);
  const [laborEdgeError, setLaborEdgeError] = useState('');
  const [laborEdgeFlaggedItems, setLaborEdgeFlaggedItems] = useState([]);
  const [laborEdgeFlaggedLoading, setLaborEdgeFlaggedLoading] = useState(false);
  const [laborEdgeFlaggedError, setLaborEdgeFlaggedError] = useState('');
  const [laborEdgeFlaggedExpanded, setLaborEdgeFlaggedExpanded] = useState(new Set());
  const [laborEdgeCleanupProgress, setLaborEdgeCleanupProgress] = useState(null);
  const [laborEdgeReviewNotes, setLaborEdgeReviewNotes] = useState({});
  const [laborEdgeReviewActionId, setLaborEdgeReviewActionId] = useState(null);
  const [laborEdgePage, setLaborEdgePage] = useState(1);
  const [laborEdgePageSize, setLaborEdgePageSize] = useState(25);
  const [laborEdgeForm, setLaborEdgeForm] = useState({
    jobId: '',
    jobIdMatchMode: 'contains',
    keyword: '',
    descStatus: '',
    location: '',
    department: '',
    featuredJob: '',
    publish: '',
  });
  const [laborEdgeFiltersAppliedAt, setLaborEdgeFiltersAppliedAt] = useState(null);
  const [laborEdgeFiltersEnabled, setLaborEdgeFiltersEnabled] = useState(true);
  const [laborEdgeSchedule, setLaborEdgeSchedule] = useState(null);
  const [laborEdgeScheduleLoading, setLaborEdgeScheduleLoading] = useState(false);
  const [laborEdgeScheduleSaving, setLaborEdgeScheduleSaving] = useState(false);
  const [laborEdgeScheduleError, setLaborEdgeScheduleError] = useState('');
  const [laborEdgeScheduleSuccess, setLaborEdgeScheduleSuccess] = useState('');
  const [laborEdgeScheduleForm, setLaborEdgeScheduleForm] = useState({
    intervalMinutes: 0,
    nightlyHourUtc: 3,
    nightlyMinuteUtc: 0,
    runOnStartup: true,
  });
  const [selectedLaborEdgePreviewJob, setSelectedLaborEdgePreviewJob] = useState(null);
  const [indeedRssStatus, setIndeedRssStatus] = useState(null);
  const [indeedRssStatusLoading, setIndeedRssStatusLoading] = useState(false);
  const [indeedRssActionLoading, setIndeedRssActionLoading] = useState(false);
  const [indeedRssResult, setIndeedRssResult] = useState(null);
  const [indeedRssError, setIndeedRssError] = useState('');
  const [indeedRssPage, setIndeedRssPage] = useState(1);
  const [indeedRssPageSize, setIndeedRssPageSize] = useState(25);
  const [indeedRssForm, setIndeedRssForm] = useState({
    keyword: '',
    location: '',
  });
  const [indeedRssFiltersAppliedAt, setIndeedRssFiltersAppliedAt] = useState(null);
  const [selectedIndeedRssPreviewJob, setSelectedIndeedRssPreviewJob] = useState(null);
  const [indeedRssMeta, setIndeedRssMeta] = useState(null);

  const jobDivaPreviewColumns = [
    'view',
    'id',
    'reference #',
    'job title',
    'company',
    'rate range',
    'desc status',
  ];

  const zohoPreviewColumns = [
    'id',
    'reference',
    'title',
    'company',
    'location',
    'department',
    'status',
    'posted date',
  ];

  const laborEdgePreviewColumns = [
    'id',
    'reference',
    'title',
    'company',
    'desc status',
    'location',
    'department',
    'status',
    'job type',
    'featured job',
    'external board',
    'hot job',
    'posted date',
    'publish',
  ];

  const jobDivaPreviewRows = useMemo(() => {
    const jobs = Array.isArray(jobDivaResult?.data?.jobs) ? jobDivaResult.data.jobs : [];
    return jobs
      .map((job, index) => {
        const raw = job?.raw || {};
        // Format epoch-ms date fields for readability
        const formatted = { ...raw };
        ['issue date', 'start date', 'end date'].forEach((key) => {
          const val = raw[key];
          if (val && Number.isFinite(Number(val))) {
            formatted[key] = new Date(Number(val)).toLocaleDateString();
          }
        });
        const cleanup = job?.descriptionCleanup;
        const descStatus = cleanup?.status === 'cleaned' || cleanup?.source === 'cleaned'
          ? 'cleaned'
          : cleanup?.status === 'pending_review'
          ? 'pending_review'
          : cleanup?.status
          ? cleanup.status
          : 'none';
        return {
          _rowId: job?.id || raw.id || raw['reference #'] || `row-${index}`,
          _job: job,
          _descStatus: descStatus,
          ...formatted,
          'rate range': job?.minRate != null
            ? (() => {
                const fmt = (v) => Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 });
                return `${fmt(job.minRate)}${job.maxRate != null ? ` \u2013 ${fmt(job.maxRate)}` : ''}${job.ratePer ? `/${job.ratePer}` : ''}`;
              })()
            : null,
        };
      });
  }, [jobDivaResult]);

  const jobDivaMeta = jobDivaResult?.data?.meta || null;
  const jobDivaCleanupSummary = jobDivaResult?.cleanupSummary || jobDivaResult?.syncSummary?.cleanupSummary || null;
  const jobDivaCleanupCompleted = Number(jobDivaCleanupProgress?.completedCandidates ?? 0);
  const jobDivaCleanupTotalCandidates = Number(jobDivaCleanupProgress?.totalCandidates ?? 0);
  const jobDivaCleanupPercent = jobDivaCleanupTotalCandidates > 0
    ? Math.min(100, Math.round((jobDivaCleanupCompleted / jobDivaCleanupTotalCandidates) * 100))
    : 0;
  const jobDivaLastApiSyncAgo = useMemo(() => {
    const ts = jobDivaStatus?.lastSyncAt;
    if (!ts) return 'Never';

    const parsed = new Date(ts);
    if (Number.isNaN(parsed.getTime())) return 'Unknown';

    const diffSeconds = Math.max(0, Math.floor((jobDivaNowMs - parsed.getTime()) / 1000));
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return `${Math.floor(diffSeconds / 86400)}d ago`;
  }, [jobDivaStatus?.lastSyncAt, jobDivaNowMs]);

  const zohoPreviewRows = useMemo(() => {
    const jobs = Array.isArray(zohoResult?.data?.jobs)
      ? zohoResult.data.jobs
      : (Array.isArray(zohoResult?.data) ? zohoResult.data : []);
    const publishFilter = zohoForm.publish;
    const filtered = publishFilter === ''
      ? jobs
      : jobs.filter((job) => {
          const isPublished = job?.publish === true;
          return publishFilter === 'true' ? isPublished : !isPublished;
        });
    return filtered.map((job, index) => ({
      _rowId: job?.id || job?.reference || `zoho-row-${index}`,
      _job: job,
      id: job?.id || '—',
      reference: job?.reference || '—',
      title: job?.title || '—',
      company: job?.company || '—',
      location: job?.location || '—',
      department: job?.department || '—',
      status: job?.status || '—',
      'posted date': job?.postedDate ? new Date(job.postedDate).toLocaleDateString() : '—',
    }));
  }, [zohoResult, zohoForm.publish]);

  const laborEdgePreviewRows = useMemo(() => {
    const jobs = Array.isArray(laborEdgeResult?.data?.jobs)
      ? laborEdgeResult.data.jobs
      : (Array.isArray(laborEdgeResult?.data) ? laborEdgeResult.data : []);

    const readRawDiagnostic = (raw, keys, fallback = '—') => {
      for (const key of keys) {
        if (raw?.[key] != null && String(raw[key]).trim() !== '') {
          return String(raw[key]);
        }
      }

      return fallback;
    };

    return jobs.map((job, index) => {
      const raw = job?.raw || {};
      const cleanup = job?.descriptionCleanup;
      const descStatus = cleanup?.status === 'cleaned' || cleanup?.source === 'cleaned' || cleanup?.source === 'ai_cleaned'
        ? 'cleaned'
        : cleanup?.status === 'pending_review'
        ? 'pending_review'
        : cleanup?.status
        ? cleanup.status
        : 'none';

      return {
        _rowId: job?.id || job?.reference || `laboredge-row-${index}`,
        _job: job,
        _descStatus: descStatus,
        id: job?.id || '—',
        reference: job?.reference || '—',
        title: job?.title || '—',
        company: job?.company || '—',
        'desc status': descStatus,
        location: job?.location || '—',
        department: job?.department || '—',
        status: job?.status || '—',
        'job type': readRawDiagnostic(raw, ['jobType', 'job type', 'positionType', 'employmentType']),
        'featured job': readRawDiagnostic(raw, ['featuredJob', 'featured', 'isFeatured']),
        'external board': readRawDiagnostic(raw, ['displayOnExternalJobBoard', 'displayonexternaljobboard']),
        'hot job': readRawDiagnostic(raw, ['hotJob', 'hotjob']),
        'posted date': job?.postedDate ? new Date(job.postedDate).toLocaleDateString() : '—',
        publish: job?.publish === true ? 'true' : job?.publish === false ? 'false' : '—',
      };
    });
  }, [laborEdgeResult]);

  const laborEdgeMeta = (laborEdgeResult?.data && !Array.isArray(laborEdgeResult.data) && laborEdgeResult.data.meta)
    ? laborEdgeResult.data.meta
    : null;
  const laborEdgeCleanupSummary = laborEdgeResult?.cleanupSummary || laborEdgeResult?.syncSummary?.cleanupSummary || null;
  const laborEdgeCleanupCompleted = Number(laborEdgeCleanupProgress?.completedCandidates ?? 0);
  const laborEdgeCleanupTotalCandidates = Number(laborEdgeCleanupProgress?.totalCandidates ?? 0);
  const laborEdgeCleanupPercent = laborEdgeCleanupTotalCandidates > 0
    ? Math.min(100, Math.round((laborEdgeCleanupCompleted / laborEdgeCleanupTotalCandidates) * 100))
    : 0;

  const indeedRssPreviewRows = useMemo(() => {
    const jobs = Array.isArray(indeedRssResult?.data?.jobs)
      ? indeedRssResult.data.jobs
      : (Array.isArray(indeedRssResult?.data) ? indeedRssResult.data : []);

    return jobs.map((job, index) => ({
      _rowId: job?.id || `indeed-rss-row-${index}`,
      _job: job,
      id: job?.id || '—',
      title: job?.title || '—',
      company: job?.company || '—',
      location: job?.location || '—',
      'posted date': job?.postedDate ? new Date(job.postedDate).toLocaleDateString() : '—',
    }));
  }, [indeedRssResult]);

  const indeedRssLastApiSyncAgo = indeedRssStatus?.lastSyncTime
    ? `${Math.round((Date.now() - new Date(indeedRssStatus.lastSyncTime).getTime()) / 1000 / 60)} minutes ago`
    : 'Never';

  const laborEdgeLastApiSyncAgo = useMemo(() => {
    const ts = laborEdgeStatus?.lastSyncAt;
    if (!ts) return 'Never';

    const parsed = new Date(ts);
    if (Number.isNaN(parsed.getTime())) return 'Unknown';

    const diffSeconds = Math.max(0, Math.floor((jobDivaNowMs - parsed.getTime()) / 1000));
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return `${Math.floor(diffSeconds / 86400)}d ago`;
  }, [laborEdgeStatus?.lastSyncAt, jobDivaNowMs]);

  const zohoMeta = (zohoResult?.data && !Array.isArray(zohoResult.data) && zohoResult.data.meta)
    ? zohoResult.data.meta
    : null;

  const zohoLastApiSyncAgo = useMemo(() => {
    const ts = zohoStatus?.lastSyncAt;
    if (!ts) return 'Never';

    const parsed = new Date(ts);
    if (Number.isNaN(parsed.getTime())) return 'Unknown';

    const diffSeconds = Math.max(0, Math.floor((jobDivaNowMs - parsed.getTime()) / 1000));
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return `${Math.floor(diffSeconds / 86400)}d ago`;
  }, [zohoStatus?.lastSyncAt, jobDivaNowMs]);

  useEffect(() => {
    if (user && canManageUsers) {
      fetchUsers();
      fetchAvailableRoles();
    }
  }, [user, canManageUsers]);

  useEffect(() => {
    const allowedTabs = [];
    if (canManageUsers) allowedTabs.push('users');
    if (canManagePermissions) allowedTabs.push('permissions');
    if (canOperateEmailWorker) {
      allowedTabs.push('email-diagnostics');
      allowedTabs.push('rsvp-diagnostics');
    }
    if (canRunJobDivaDiagnostics) {
      allowedTabs.push('jobdiva-diagnostics');
    }
    if (canRunZohoDiagnostics) {
      allowedTabs.push('zoho-diagnostics');
      allowedTabs.push('zoho-jobdiva-etl');
      allowedTabs.push('zoho-maintenance');
    }
    if (canRunLaborEdgeDiagnostics) {
      allowedTabs.push('laboredge-diagnostics');
    }
    if (canRunIndeedRssDiagnostics) {
      allowedTabs.push('indeed-rss-diagnostics');
    }

    if (allowedTabs.length === 0) {
      return;
    }

    if (!allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0]);
    }
  }, [activeTab, canManageUsers, canManagePermissions, canOperateEmailWorker, canRunJobDivaDiagnostics, canRunZohoDiagnostics, canRunLaborEdgeDiagnostics, canRunIndeedRssDiagnostics]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdown && !e.target.closest('[data-role-dropdown]')) {
        setOpenDropdown(null);
      }
      if (openRoleFilterDropdown && !e.target.closest('[data-role-filter-dropdown]')) {
        setOpenRoleFilterDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown, openRoleFilterDropdown]);

  useEffect(() => {
    if (!canOperateEmailWorker) return;

    if (activeTab === 'email-diagnostics') {
      if (emailDiagnostics.length === 0) {
        fetchEmailDiagnostics();
      }
      fetchEmailWorkerStatus();
    }

    if (activeTab === 'rsvp-diagnostics') {
      if (rsvpAccessDiagnostics.length === 0) {
        fetchRsvpAccessDiagnostics();
      }
      if (rsvpAccessRecipients.length === 0) {
        fetchRsvpAccessRecipients('');
      }
    }

  }, [activeTab, canOperateEmailWorker]);

  useEffect(() => {
    if (activeTab !== 'jobdiva-diagnostics' || jobDivaSubTab !== 'jobs' || !canRunJobDivaDiagnostics) {
      return;
    }

    fetchJobDivaStatus();
    fetchJobDivaSchedule();
    fetchJobDivaFlaggedItems();
    fetchJobDivaCleanupProgress();
    executeJobDivaRequest({
      endpoint: '/admin/jobdiva/jobs/cached',
      page: 1,
      pageSize: jobDivaPageSize,
      action: 'call-api',
      failureMessage: 'Failed to load cached JobDiva jobs',
    });
    jobDivaInitialLoadRef.current = true;
  }, [activeTab, jobDivaSubTab, canRunJobDivaDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'jobdiva-diagnostics' || jobDivaSubTab !== 'jobs' || !canRunJobDivaDiagnostics || !jobDivaFiltersEnabled) {
      return;
    }

    if (jobDivaInitialLoadRef.current) {
      jobDivaInitialLoadRef.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      setJobDivaFiltersAppliedAt(new Date());
      executeJobDivaRequest({
        endpoint: '/admin/jobdiva/jobs/cached',
        page: 1,
        pageSize: jobDivaPageSize,
        action: 'call-api',
        failureMessage: 'Failed to load cached JobDiva jobs',
      });
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [jobDivaForm, activeTab, jobDivaSubTab, canRunJobDivaDiagnostics, jobDivaFiltersEnabled]);

  useEffect(() => {
    if (activeTab !== 'zoho-diagnostics' || !canRunZohoDiagnostics) {
      return;
    }

    fetchZohoStatus();
    executeZohoRequest({
      endpoint: '/admin/zoho/jobs/cached',
      page: 1,
      pageSize: zohoPageSize,
      failureMessage: 'Failed to load cached Zoho jobs',
    });
  }, [activeTab, canRunZohoDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'zoho-diagnostics' || !canRunZohoDiagnostics) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setZohoFiltersAppliedAt(new Date());
      executeZohoRequest({
        endpoint: '/admin/zoho/jobs/cached',
        page: 1,
        pageSize: zohoPageSize,
        failureMessage: 'Failed to load cached Zoho jobs',
      });
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [zohoForm, activeTab, canRunZohoDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'zoho-maintenance' || !canRunZohoDiagnostics) {
      return;
    }

    setZohoMaintenanceSubTab('ticket-dashboard');
    fetchZohoMaintenanceStatus();
    fetchZohoMaintenanceHistory();
    fetchZohoMaintenanceTicketSummary();
    fetchZohoMaintenanceTickets();
  }, [activeTab, canRunZohoDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'zoho-maintenance' || !canRunZohoDiagnostics) {
      return;
    }

    fetchZohoMaintenanceTickets(zohoMaintenanceTicketStatus);
  }, [zohoMaintenanceTicketStatus, activeTab, canRunZohoDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'laboredge-diagnostics' || !canRunLaborEdgeDiagnostics) {
      return;
    }

    fetchLaborEdgeStatus();
    fetchLaborEdgeSchedule();
    fetchLaborEdgeFlaggedItems();
    fetchLaborEdgeCleanupProgress();
    executeLaborEdgeRequest({
      endpoint: '/admin/laboredge/jobs/cached',
      page: 1,
      pageSize: laborEdgePageSize,
      failureMessage: 'Failed to load cached LaborEdge jobs',
    });
  }, [activeTab, canRunLaborEdgeDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'laboredge-diagnostics' || !canRunLaborEdgeDiagnostics || !laborEdgeFiltersEnabled) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setLaborEdgeFiltersAppliedAt(new Date());
      executeLaborEdgeRequest({
        endpoint: '/admin/laboredge/jobs/cached',
        page: 1,
        pageSize: laborEdgePageSize,
        failureMessage: 'Failed to load cached LaborEdge jobs',
      });
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [laborEdgeForm, activeTab, canRunLaborEdgeDiagnostics, laborEdgeFiltersEnabled]);

  useEffect(() => {
    if (!laborEdgeCleanupLoading || activeTab !== 'laboredge-diagnostics' || !canRunLaborEdgeDiagnostics) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchLaborEdgeCleanupProgress();
    }, 1500);

    return () => clearInterval(intervalId);
  }, [laborEdgeCleanupLoading, activeTab, canRunLaborEdgeDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'indeed-rss-diagnostics' || !canRunIndeedRssDiagnostics) {
      return;
    }

    fetchIndeedRssStatus();
    executeIndeedRssRequest('/api/admin/indeed-rss/jobs/cached', {
      page: 1,
      pageSize: indeedRssPageSize,
    });
  }, [activeTab, canRunIndeedRssDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'indeed-rss-diagnostics' || !canRunIndeedRssDiagnostics) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIndeedRssFiltersAppliedAt(new Date());
      executeIndeedRssRequest('/api/admin/indeed-rss/jobs/cached', {
        keyword: indeedRssForm.keyword,
        location: indeedRssForm.location,
        page: 1,
        pageSize: indeedRssPageSize,
      });
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [indeedRssForm, activeTab, canRunIndeedRssDiagnostics]);

  useEffect(() => {
    if (activeTab !== 'jobdiva-diagnostics' || !canRunJobDivaDiagnostics) {
      return;
    }

    const intervalId = setInterval(() => {
      setJobDivaNowMs(Date.now());
    }, 30000);

    return () => clearInterval(intervalId);
  }, [activeTab, canRunJobDivaDiagnostics]);

  useEffect(() => {
    if (!jobDivaCleanupLoading || activeTab !== 'jobdiva-diagnostics' || !canRunJobDivaDiagnostics) {
      return;
    }

    const intervalId = setInterval(() => {
      fetchJobDivaCleanupProgress();
    }, 1500);

    return () => clearInterval(intervalId);
  }, [jobDivaCleanupLoading, activeTab, canRunJobDivaDiagnostics]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event?.data?.type === 'zoho-oauth-complete') {
        fetchZohoStatus();
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const fetchAvailableRoles = async () => {
    try {
      const { rolePermissionsAPI } = await import('../services/rolePermissionsAPI');
      const response = await rolePermissionsAPI.getRoles();
      const roles = response.data.map(r => r.role).filter(r => r !== 'superuser');
      setAvailableRoles(roles);
    } catch (err) {
      console.error('Failed to load available roles:', err);
      setAvailableRoles(['admin', 'template_creator', 'user']);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailDiagnostics = async (filterOverrides) => {
    setLoadingEmailDiagnostics(true);
    setEmailDiagnosticsError('');

    try {
      const activeFilters = filterOverrides || emailDiagnosticFilters;
      const params = new URLSearchParams({ limit: '200' });

      if (activeFilters.recipient.trim()) {
        params.set('recipient', activeFilters.recipient.trim());
      }
      if (activeFilters.status.trim()) {
        params.set('status', activeFilters.status.trim());
      }
      if (activeFilters.sender.trim()) {
        params.set('sender', activeFilters.sender.trim());
      }

      const res = await api.get(`/admin/email-diagnostics?${params.toString()}`);
      setEmailDiagnostics(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setEmailDiagnosticsError('Failed to load email diagnostics');
    } finally {
      setLoadingEmailDiagnostics(false);
    }
  };

  const fetchEmailWorkerStatus = async () => {
    setLoadingEmailWorkerStatus(true);
    setEmailWorkerActionError('');

    try {
      const res = await api.get('/admin/email-worker/status');
      setEmailWorkerStatus(res.data || null);
    } catch (err) {
      setEmailWorkerActionError('Failed to load email worker status');
    } finally {
      setLoadingEmailWorkerStatus(false);
    }
  };

  const fetchRsvpAccessDiagnostics = async (filterOverrides) => {
    setLoadingRsvpAccessDiagnostics(true);
    setRsvpAccessDiagnosticsError('');

    try {
      const activeFilters = filterOverrides || rsvpAccessDiagnosticFilters;
      const params = new URLSearchParams({ limit: '200' });

      if (activeFilters.outcome.trim()) {
        params.set('outcome', activeFilters.outcome.trim());
      }
      if (activeFilters.action.trim()) {
        params.set('action', activeFilters.action.trim());
      }
      if (activeFilters.eventId.trim()) {
        params.set('eventId', activeFilters.eventId.trim());
      }
      if (activeFilters.likelyScanner.trim()) {
        params.set('likelyScanner', activeFilters.likelyScanner.trim());
      }
      if (activeFilters.tokenFingerprint.trim()) {
        params.set('tokenFingerprint', activeFilters.tokenFingerprint.trim());
      }
      if (activeFilters.recipientEmail.trim()) {
        params.set('recipientEmail', activeFilters.recipientEmail.trim());
      }

      const res = await api.get(`/admin/rsvp-access-diagnostics?${params.toString()}`);
      setRsvpAccessDiagnostics(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setRsvpAccessDiagnosticsError('Failed to load RSVP access diagnostics');
    } finally {
      setLoadingRsvpAccessDiagnostics(false);
    }
  };

  const fetchRsvpAccessRecipients = async (search) => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) {
        params.set('search', search.trim());
      }
      const res = await api.get(`/admin/rsvp-access-recipients?${params.toString()}`);
      setRsvpAccessRecipients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      logger.warn('Failed to fetch RSVP access recipients', err);
    }
  };

  const fetchJobDivaStatus = async () => {
    setJobDivaStatusLoading(true);
    setJobDivaError('');

    try {
      const res = await api.get('/admin/jobdiva/status');
      setJobDivaStatus(res.data || null);
    } catch (err) {
      setJobDivaError(err.response?.data?.error || 'Failed to load JobDiva status');
    } finally {
      setJobDivaStatusLoading(false);
    }
  };

  const loadJobDivaCandidate = async (event) => {
    event?.preventDefault();
    const candidateId = String(jobDivaCandidateId || '').trim();
    if (!candidateId) {
      setJobDivaCandidateError('Enter a JobDiva candidate ID.');
      setJobDivaCandidateResult(null);
      return;
    }

    setJobDivaCandidateLoading(true);
    setJobDivaCandidateError('');
    setJobDivaCandidateResult(null);
    try {
      const res = await api.get(`/admin/jobdiva/candidates/${encodeURIComponent(candidateId)}/raw`);
      setJobDivaCandidateResult(res.data || null);
    } catch (err) {
      setJobDivaCandidateError(err.response?.data?.error?.message || err.response?.data?.error || 'Failed to load JobDiva candidate data.');
    } finally {
      setJobDivaCandidateLoading(false);
    }
  };

  const fetchJobDivaSchedule = async () => {
    setJobDivaScheduleLoading(true);
    setJobDivaScheduleError('');

    try {
      const res = await api.get('/admin/jobdiva/refresh-schedule');
      const schedule = res.data?.schedule || null;
      setJobDivaSchedule(schedule);
      if (schedule) {
        setJobDivaScheduleForm({
          intervalMinutes: Number(schedule.intervalMinutes || 0),
          nightlyHourUtc: Number(schedule.nightlyHourUtc || 2),
          nightlyMinuteUtc: Number(schedule.nightlyMinuteUtc || 0),
          runOnStartup: Boolean(schedule.runOnStartup)
        });
      }
    } catch (err) {
      setJobDivaScheduleError(err.response?.data?.error || 'Failed to load JobDiva refresh schedule');
    } finally {
      setJobDivaScheduleLoading(false);
    }
  };

  const handleJobDivaScheduleFormChange = (field, value) => {
    setJobDivaScheduleForm((prev) => ({
      ...prev,
      [field]: field === 'runOnStartup' ? Boolean(value) : Number(value),
    }));
  };

  const saveJobDivaSchedule = async () => {
    setJobDivaScheduleSaving(true);
    setJobDivaScheduleError('');
    setJobDivaScheduleSuccess('');

    try {
      const res = await api.put('/admin/jobdiva/refresh-schedule', jobDivaScheduleForm);
      setJobDivaSchedule(res.data?.schedule || null);
      setJobDivaScheduleSuccess('JobDiva refresh schedule updated.');
    } catch (err) {
      setJobDivaScheduleError(err.response?.data?.error || 'Failed to update JobDiva refresh schedule');
    } finally {
      setJobDivaScheduleSaving(false);
    }
  };

  const runJobDivaRefreshNow = async () => {
    setJobDivaTestLoading(true);
    setJobDivaError('');

    try {
      const res = await api.post('/admin/jobdiva/refresh-now');
      setJobDivaResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      await Promise.all([
        fetchJobDivaStatus(),
        fetchJobDivaSchedule(),
        executeJobDivaRequest({
          endpoint: '/admin/jobdiva/jobs/cached',
          page: 1,
          pageSize: jobDivaPageSize,
          action: 'call-api',
          failureMessage: 'Failed to load cached JobDiva jobs',
        })
      ]);
    } catch (err) {
      const data = err.response?.data;
      setJobDivaResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setJobDivaError(data?.error?.message || data?.error || 'Failed to refresh JobDiva cache');
    } finally {
      setJobDivaTestLoading(false);
    }
  };

  const fetchZohoStatus = async () => {
    setZohoStatusLoading(true);
    setZohoError('');

    try {
      const res = await api.get('/admin/zoho/status');
      setZohoStatus(res.data || null);
    } catch (err) {
      setZohoError(err.response?.data?.error || 'Failed to load Zoho status');
    } finally {
      setZohoStatusLoading(false);
    }
  };

  const fetchZohoMaintenanceStatus = async () => {
    setZohoMaintenanceLoading(true);
    setZohoMaintenanceError('');

    try {
      const res = await api.get('/admin/zoho/monthly-maintenance/status');
      const status = res.data || null;
      setZohoMaintenanceStatus(status);
      setZohoMaintenanceForm({
        enabled: Boolean(status?.enabled),
        isPaused: Boolean(status?.isPaused),
        runDayOfMonth: Number(status?.schedule?.runDayOfMonth || 1),
        runHourUtc: Number(status?.schedule?.runHourUtc || 3),
        runMinuteUtc: Number(status?.schedule?.runMinuteUtc || 0),
      });
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to load Zoho monthly maintenance status');
    } finally {
      setZohoMaintenanceLoading(false);
    }
  };

  const fetchZohoMaintenanceHistory = async () => {
    setZohoMaintenanceHistoryLoading(true);
    setZohoMaintenanceHistoryError('');

    try {
      const res = await api.get('/admin/zoho/monthly-maintenance/history?limit=25');
      setZohoMaintenanceHistory(Array.isArray(res.data?.history) ? res.data.history : []);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to load Zoho monthly maintenance history';
      const details = err.response?.data?.details;
      setZohoMaintenanceHistoryError(details ? `${message}\n${typeof details === 'string' ? details : JSON.stringify(details, null, 2)}` : message);
    } finally {
      setZohoMaintenanceHistoryLoading(false);
    }
  };

  const fetchZohoMaintenanceTicketSummary = async () => {
    try {
      const res = await api.get('/admin/zoho/monthly-maintenance/tickets/summary');
      setZohoMaintenanceTicketSummary(res.data?.summary || null);
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to load SDM monthly ticket summary');
    }
  };

  const fetchZohoMaintenanceTickets = async (statusFilter = zohoMaintenanceTicketStatus) => {
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (statusFilter) {
        params.set('status', statusFilter);
      }
      const res = await api.get(`/admin/zoho/monthly-maintenance/tickets?${params.toString()}`);
      setZohoMaintenanceTickets(Array.isArray(res.data?.tickets) ? res.data.tickets : []);
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to load SDM monthly tickets');
    }
  };

  const handleZohoMaintenanceFormChange = (field, value) => {
    setZohoMaintenanceForm((prev) => ({
      ...prev,
      [field]: field === 'enabled' || field === 'isPaused' ? Boolean(value) : Number(value),
    }));
  };

  const refreshZohoMaintenanceData = async () => {
    await Promise.all([
      fetchZohoMaintenanceStatus(),
      fetchZohoMaintenanceHistory(),
      fetchZohoMaintenanceTicketSummary(),
      fetchZohoMaintenanceTickets()
    ]);
  };

  const saveZohoMaintenanceSchedule = async () => {
    setZohoMaintenanceActionLoading(true);
    setZohoMaintenanceError('');
    setZohoMaintenanceSuccess('');

    try {
      const res = await api.put('/admin/zoho/monthly-maintenance/schedule', zohoMaintenanceForm);
      setZohoMaintenanceStatus(res.data || null);
      setZohoMaintenanceSuccess('Monthly maintenance schedule updated.');
      await fetchZohoMaintenanceHistory();
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to update Zoho monthly maintenance schedule');
    } finally {
      setZohoMaintenanceActionLoading(false);
    }
  };

  const runZohoMaintenanceNow = async () => {
    setZohoMaintenanceActionLoading(true);
    setZohoMaintenanceError('');
    setZohoMaintenanceSuccess('');

    try {
      const res = await api.post('/admin/zoho/monthly-maintenance/run', { reason: 'manual' });
      setZohoMaintenanceSuccess(`Maintenance run completed: ${res.data?.totalUpstreamJobs ?? 0} upstream jobs processed.`);
      await refreshZohoMaintenanceData();
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to run Zoho monthly maintenance');
      await fetchZohoMaintenanceHistory();
    } finally {
      setZohoMaintenanceActionLoading(false);
    }
  };

  const pauseZohoMaintenance = async () => {
    setZohoMaintenanceActionLoading(true);
    setZohoMaintenanceError('');
    setZohoMaintenanceSuccess('');

    try {
      const res = await api.post('/admin/zoho/monthly-maintenance/pause');
      setZohoMaintenanceStatus(res.data || null);
      setZohoMaintenanceForm((prev) => ({ ...prev, isPaused: true }));
      setZohoMaintenanceSuccess('Monthly maintenance paused.');
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to pause Zoho monthly maintenance');
    } finally {
      setZohoMaintenanceActionLoading(false);
    }
  };

  const resumeZohoMaintenance = async () => {
    setZohoMaintenanceActionLoading(true);
    setZohoMaintenanceError('');
    setZohoMaintenanceSuccess('');

    try {
      const res = await api.post('/admin/zoho/monthly-maintenance/resume');
      setZohoMaintenanceStatus(res.data || null);
      setZohoMaintenanceForm((prev) => ({ ...prev, isPaused: false }));
      setZohoMaintenanceSuccess('Monthly maintenance resumed.');
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to resume Zoho monthly maintenance');
    } finally {
      setZohoMaintenanceActionLoading(false);
    }
  };

  const repairZohoMaintenanceCredentials = async () => {
    setZohoMaintenanceActionLoading(true);
    setZohoMaintenanceError('');
    setZohoMaintenanceSuccess('');

    try {
      await api.post('/admin/zoho/monthly-maintenance/repair-credentials');
      setZohoMaintenanceSuccess('Zoho credentials repaired successfully.');
      await fetchZohoMaintenanceStatus();
    } catch (err) {
      setZohoMaintenanceError(err.response?.data?.error || 'Failed to repair Zoho maintenance credentials');
    } finally {
      setZohoMaintenanceActionLoading(false);
    }
  };

  const fetchJobDivaFlaggedItems = async () => {
    setJobDivaFlaggedLoading(true);
    setJobDivaFlaggedError('');

    try {
      const res = await api.get('/admin/jobdiva/cleanup/flagged?limit=25');
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setJobDivaFlaggedItems(items);
      setJobDivaReviewNotes((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          if (next[item.cleanupId] == null) {
            next[item.cleanupId] = item.reviewReason || '';
          }
        });
        return next;
      });
    } catch (err) {
      setJobDivaFlaggedError(err.response?.data?.error || 'Failed to load flagged JobDiva cleanups');
    } finally {
      setJobDivaFlaggedLoading(false);
    }
  };

  const fetchJobDivaCleanupProgress = async () => {
    try {
      const res = await api.get('/admin/jobdiva/cleanup/progress');
      setJobDivaCleanupProgress(res.data || null);
    } catch (_err) {
      // Intentionally silent; progress polling should not block diagnostics usage.
    }
  };

  const handleJobDivaFormChange = (field, value) => {
    setJobDivaForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const executeJobDivaRequest = async ({
    endpoint,
    page = jobDivaPage,
    pageSize = jobDivaPageSize,
    action = 'call-api',
    failureMessage = 'JobDiva request failed',
  }) => {
    const payload = {
      ...(jobDivaFiltersEnabled ? {
        reference: jobDivaForm.reference,
        keyword: jobDivaForm.keyword,
        descStatus: jobDivaForm.descStatus,
        company: jobDivaForm.company,
        division: jobDivaForm.division,
        priority: jobDivaForm.priority,
        ...(jobDivaForm.posttoportal !== '' ? { posttoportal: jobDivaForm.posttoportal === 'true' } : {}),
      } : {}),
      page,
      pageSize,
    };

    if (action === 'reset') {
      setJobDivaResetLoading(true);
    } else if (action === 'cleanup') {
      setJobDivaCleanupLoading(true);
    } else {
      setJobDivaTestLoading(true);
    }

    setJobDivaError('');

    try {
      const res = await api.post(endpoint, payload);
      setJobDivaResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      setJobDivaPage(page);
      setJobDivaPageSize(pageSize);
      await Promise.all([fetchJobDivaStatus(), fetchJobDivaFlaggedItems()]);
    } catch (err) {
      const data = err.response?.data;
      setJobDivaResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setJobDivaError(data?.error?.message || data?.error || failureMessage);
      await Promise.all([fetchJobDivaStatus(), fetchJobDivaFlaggedItems()]);
    } finally {
      if (action === 'reset') {
        setJobDivaResetLoading(false);
      } else if (action === 'cleanup') {
        setJobDivaCleanupLoading(false);
      } else {
        setJobDivaTestLoading(false);
      }
    }
  };

  const handleZohoFormChange = (field, value) => {
    setZohoForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const executeZohoRequest = async ({
    endpoint,
    page = zohoPage,
    pageSize = zohoPageSize,
    failureMessage = 'Zoho request failed',
  }) => {
    const payload = {
      keyword: zohoForm.keyword,
      location: zohoForm.location,
      department: zohoForm.department,
      page,
      pageSize,
    };

    setZohoActionLoading(true);
    setZohoError('');

    try {
      const res = await api.post(endpoint, payload);
      setZohoResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      setZohoPage(page);
      setZohoPageSize(pageSize);
      await fetchZohoStatus();
    } catch (err) {
      const data = err.response?.data;
      setZohoResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      // Show detailed error information
      const errorMsg = data?.error?.message 
        || data?.error?.details 
        || data?.error 
        || failureMessage;
      setZohoError(errorMsg);
      await fetchZohoStatus();
    } finally {
      setZohoActionLoading(false);
    }
  };

  const runZohoCallApi = async () => {
    await executeZohoRequest({
      endpoint: '/admin/zoho/call-api',
      page: 1,
      pageSize: zohoPageSize,
      failureMessage: 'Zoho API call failed',
    });
  };

  const applyZohoFilters = async () => {
    setZohoFiltersAppliedAt(new Date());
    await executeZohoRequest({
      endpoint: '/admin/zoho/jobs/cached',
      page: 1,
      pageSize: zohoPageSize,
      failureMessage: 'Failed to load cached Zoho jobs',
    });
  };

  const refreshZohoToken = async () => {
    setZohoActionLoading(true);
    setZohoError('');

    try {
      const res = await api.post('/admin/zoho/refresh-token');
      setZohoResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      await fetchZohoStatus();
    } catch (err) {
      const data = err.response?.data;
      setZohoResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setZohoError(data?.error?.message || data?.error || 'Failed to refresh Zoho token');
      await fetchZohoStatus();
    } finally {
      setZohoActionLoading(false);
    }
  };

  const bootstrapZohoAuthCode = async () => {
    const code = String(zohoAuthorizationCode || '').trim();
    if (!code) {
      setZohoError('Authorization code is required to bootstrap Zoho tokens.');
      return;
    }

    setZohoOAuthLoading(true);
    setZohoError('');

    try {
      const res = await api.post('/admin/zoho/bootstrap-auth-code', {
        authorizationCode: code,
      });

      setZohoResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      setZohoAuthorizationCode('');
      await fetchZohoStatus();
    } catch (err) {
      const data = err.response?.data;
      setZohoResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setZohoError(data?.error?.message || data?.error?.details || data?.error || 'Failed to bootstrap Zoho authorization code');
      await fetchZohoStatus();
    } finally {
      setZohoOAuthLoading(false);
    }
  };

  const launchZohoOAuth = async () => {
    setZohoOAuthLoading(true);
    setZohoError('');

    try {
      const redirectUri = String(zohoRedirectUriInput || '').trim();
      const res = await api.get('/admin/zoho/oauth/authorize-url', {
        params: redirectUri ? { redirectUri } : undefined,
      });
      const authorizeUrl = res.data?.authorizeUrl;
      const effectiveRedirectUri = String(res.data?.redirectUri || '').trim();
      setZohoAuthorizeUrl(authorizeUrl || '');
      if (effectiveRedirectUri) {
        setZohoRedirectUriInput(effectiveRedirectUri);
      }

      if (!authorizeUrl) {
        throw new Error('Zoho authorize URL was not returned.');
      }

      window.open(authorizeUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const data = err.response?.data;
      setZohoError(data?.error?.message || data?.error?.details || data?.error || err.message || 'Failed to launch Zoho OAuth flow');
    } finally {
      setZohoOAuthLoading(false);
    }
  };

  const fetchZohoMetadata = async () => {
    setZohoMetadataLoading(true);
    setZohoError('');

    try {
      const res = await api.get('/admin/zoho/metadata');
      setZohoMetadata(res.data?.data || null);
    } catch (err) {
      setZohoError(err.response?.data?.error?.message || err.response?.data?.error || 'Failed to load Zoho metadata');
    } finally {
      setZohoMetadataLoading(false);
    }
  };

  const fetchLaborEdgeStatus = async () => {
    setLaborEdgeStatusLoading(true);
    setLaborEdgeError('');

    try {
      const res = await api.get('/admin/laboredge/status');
      setLaborEdgeStatus(res.data || null);
    } catch (err) {
      setLaborEdgeError(err.response?.data?.error || 'Failed to load LaborEdge status');
    } finally {
      setLaborEdgeStatusLoading(false);
    }
  };

  const fetchLaborEdgeSchedule = async () => {
    setLaborEdgeScheduleLoading(true);
    setLaborEdgeScheduleError('');

    try {
      const res = await api.get('/admin/laboredge/refresh-schedule');
      const schedule = res.data?.schedule || null;
      setLaborEdgeSchedule(schedule);
      if (schedule) {
        setLaborEdgeScheduleForm({
          intervalMinutes: Number(schedule.intervalMinutes || 0),
          nightlyHourUtc: Number(schedule.nightlyHourUtc || 3),
          nightlyMinuteUtc: Number(schedule.nightlyMinuteUtc || 0),
          runOnStartup: Boolean(schedule.runOnStartup)
        });
      }
    } catch (err) {
      setLaborEdgeScheduleError(err.response?.data?.error || 'Failed to load LaborEdge refresh schedule');
    } finally {
      setLaborEdgeScheduleLoading(false);
    }
  };

  const fetchLaborEdgeFlaggedItems = async () => {
    setLaborEdgeFlaggedLoading(true);
    setLaborEdgeFlaggedError('');

    try {
      const res = await api.get('/admin/laboredge/cleanup/flagged?limit=25');
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setLaborEdgeFlaggedItems(items);
      setLaborEdgeReviewNotes((prev) => {
        const next = { ...prev };
        items.forEach((item) => {
          if (next[item.cleanupId] == null) {
            next[item.cleanupId] = item.reviewReason || '';
          }
        });
        return next;
      });
    } catch (err) {
      setLaborEdgeFlaggedError(err.response?.data?.error || 'Failed to load flagged LaborEdge cleanups');
    } finally {
      setLaborEdgeFlaggedLoading(false);
    }
  };

  const fetchLaborEdgeCleanupProgress = async () => {
    try {
      const res = await api.get('/admin/laboredge/cleanup/progress');
      setLaborEdgeCleanupProgress(res.data || null);
    } catch (_err) {
      // Intentionally silent; progress polling should not block diagnostics usage.
    }
  };

  const handleLaborEdgeScheduleFormChange = (field, value) => {
    setLaborEdgeScheduleForm((prev) => ({
      ...prev,
      [field]: field === 'runOnStartup' ? Boolean(value) : Number(value),
    }));
  };

  const saveLaborEdgeSchedule = async () => {
    setLaborEdgeScheduleSaving(true);
    setLaborEdgeScheduleError('');
    setLaborEdgeScheduleSuccess('');

    try {
      const res = await api.put('/admin/laboredge/refresh-schedule', laborEdgeScheduleForm);
      setLaborEdgeSchedule(res.data?.schedule || null);
      setLaborEdgeScheduleSuccess('LaborEdge refresh schedule updated.');
    } catch (err) {
      setLaborEdgeScheduleError(err.response?.data?.error || 'Failed to update LaborEdge refresh schedule');
    } finally {
      setLaborEdgeScheduleSaving(false);
    }
  };

  const runLaborEdgeRefreshNow = async () => {
    setLaborEdgeActionLoading(true);
    setLaborEdgeError('');

    try {
      const res = await api.post('/admin/laboredge/refresh-now');
      setLaborEdgeResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      await Promise.all([
        fetchLaborEdgeStatus(),
        fetchLaborEdgeSchedule(),
        executeLaborEdgeRequest({
          endpoint: '/admin/laboredge/jobs/cached',
          page: 1,
          pageSize: laborEdgePageSize,
          failureMessage: 'Failed to load cached LaborEdge jobs',
        })
      ]);
    } catch (err) {
      const data = err.response?.data;
      setLaborEdgeResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setLaborEdgeError(data?.error?.message || data?.error || 'Failed to refresh LaborEdge cache');
    } finally {
      setLaborEdgeActionLoading(false);
    }
  };

  const handleLaborEdgeFormChange = (field, value) => {
    setLaborEdgeForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const executeLaborEdgeRequest = async ({
    endpoint,
    page = laborEdgePage,
    pageSize = laborEdgePageSize,
    failureMessage = 'LaborEdge request failed',
  }) => {
    const payload = {
      ...(laborEdgeFiltersEnabled ? {
        jobId: laborEdgeForm.jobId,
        jobIdMatchMode: laborEdgeForm.jobIdMatchMode,
        keyword: laborEdgeForm.keyword,
        descStatus: laborEdgeForm.descStatus,
        location: laborEdgeForm.location,
        department: laborEdgeForm.department,
        featuredJob: laborEdgeForm.featuredJob,
        publish: laborEdgeForm.publish,
        displayOnExternalJobBoard: laborEdgeForm.publish,
      } : {}),
      page,
      pageSize,
    };

    setLaborEdgeActionLoading(true);
    setLaborEdgeError('');

    try {
      const res = await api.post(endpoint, payload);
      setLaborEdgeResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      setLaborEdgePage(page);
      setLaborEdgePageSize(pageSize);
      await fetchLaborEdgeStatus();
    } catch (err) {
      const data = err.response?.data;
      setLaborEdgeResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setLaborEdgeError(data?.error?.message || data?.error || failureMessage);
      await fetchLaborEdgeStatus();
    } finally {
      setLaborEdgeActionLoading(false);
    }
  };

  const runLaborEdgeCallApi = async () => {
    await executeLaborEdgeRequest({
      endpoint: '/admin/laboredge/call-api',
      page: 1,
      pageSize: laborEdgePageSize,
      failureMessage: 'LaborEdge API call failed',
    });
  };

  const applyLaborEdgeFilters = async () => {
    setLaborEdgeFiltersAppliedAt(new Date());
    await executeLaborEdgeRequest({
      endpoint: '/admin/laboredge/jobs/cached',
      page: 1,
      pageSize: laborEdgePageSize,
      failureMessage: 'Failed to load cached LaborEdge jobs',
    });
  };

  const runLaborEdgeCleanup = async () => {
    setLaborEdgeCleanupLoading(true);
    setLaborEdgeCleanupProgress(null);
    setLaborEdgeError('');

    try {
      const payload = {
        ...(laborEdgeFiltersEnabled ? {
          jobId: laborEdgeForm.jobId,
          jobIdMatchMode: laborEdgeForm.jobIdMatchMode,
          keyword: laborEdgeForm.keyword,
          descStatus: laborEdgeForm.descStatus,
          location: laborEdgeForm.location,
          department: laborEdgeForm.department,
          featuredJob: laborEdgeForm.featuredJob,
          publish: laborEdgeForm.publish,
          displayOnExternalJobBoard: laborEdgeForm.publish,
        } : {}),
        page: 1,
        pageSize: laborEdgePageSize,
      };

      const res = await api.post('/admin/laboredge/cleanup/run', payload);
      setLaborEdgeResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      await Promise.all([
        fetchLaborEdgeStatus(),
        fetchLaborEdgeFlaggedItems(),
        executeLaborEdgeRequest({
          endpoint: '/admin/laboredge/jobs/cached',
          page: 1,
          pageSize: laborEdgePageSize,
          failureMessage: 'Failed to load cached LaborEdge jobs',
        })
      ]);
    } catch (err) {
      const data = err.response?.data;
      setLaborEdgeResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setLaborEdgeError(data?.error?.message || data?.error || 'Failed to clean LaborEdge descriptions');
      await Promise.all([fetchLaborEdgeStatus(), fetchLaborEdgeFlaggedItems()]);
    } finally {
      setLaborEdgeCleanupLoading(false);
      await fetchLaborEdgeCleanupProgress();
    }
  };

  const resetLaborEdgeCleanups = async () => {
    const confirmed = window.confirm(
      'This will delete all rows from LaborEdgeDescriptionCleanups. Cached LaborEdge jobs will remain. Continue?'
    );

    if (!confirmed) {
      return;
    }

    setLaborEdgeResetLoading(true);
    setLaborEdgeError('');

    try {
      const payload = {
        ...(laborEdgeFiltersEnabled ? {
          jobId: laborEdgeForm.jobId,
          jobIdMatchMode: laborEdgeForm.jobIdMatchMode,
          keyword: laborEdgeForm.keyword,
          descStatus: laborEdgeForm.descStatus,
          location: laborEdgeForm.location,
          department: laborEdgeForm.department,
          featuredJob: laborEdgeForm.featuredJob,
          publish: laborEdgeForm.publish,
          displayOnExternalJobBoard: laborEdgeForm.publish,
        } : {}),
        page: 1,
        pageSize: laborEdgePageSize,
      };

      const res = await api.post('/admin/laboredge/cleanup/reset', payload);
      setLaborEdgeResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      await Promise.all([
        fetchLaborEdgeStatus(),
        fetchLaborEdgeFlaggedItems(),
        executeLaborEdgeRequest({
          endpoint: '/admin/laboredge/jobs/cached',
          page: 1,
          pageSize: laborEdgePageSize,
          failureMessage: 'Failed to load cached LaborEdge jobs',
        })
      ]);
    } catch (err) {
      const data = err.response?.data;
      setLaborEdgeResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setLaborEdgeError(data?.error?.message || data?.error || 'Failed to reset LaborEdge cleanup table');
      await Promise.all([fetchLaborEdgeStatus(), fetchLaborEdgeFlaggedItems()]);
    } finally {
      setLaborEdgeResetLoading(false);
      await fetchLaborEdgeCleanupProgress();
    }
  };

  const handleLaborEdgeReviewNoteChange = (cleanupId, value) => {
    setLaborEdgeReviewNotes((prev) => ({
      ...prev,
      [cleanupId]: value,
    }));
  };

  const handleLaborEdgeCleanupReview = async (cleanupId, approvalStatus) => {
    setLaborEdgeReviewActionId(cleanupId);
    setLaborEdgeFlaggedError('');

    try {
      await api.patch(`/admin/laboredge/cleanup/${cleanupId}/review`, {
        approvalStatus,
        reviewReason: String(laborEdgeReviewNotes[cleanupId] || '').trim(),
      });
      await fetchLaborEdgeFlaggedItems();
      await executeLaborEdgeRequest({
        endpoint: '/admin/laboredge/jobs/cached',
        page: laborEdgePage,
        pageSize: laborEdgePageSize,
        failureMessage: 'Failed to load cached LaborEdge jobs',
      });
    } catch (err) {
      setLaborEdgeFlaggedError(err.response?.data?.error || 'Failed to update LaborEdge cleanup review');
    } finally {
      setLaborEdgeReviewActionId(null);
    }
  };

  const refreshLaborEdgeToken = async () => {
    setLaborEdgeActionLoading(true);
    setLaborEdgeError('');

    try {
      const res = await api.post('/admin/laboredge/refresh-token');
      setLaborEdgeResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      await fetchLaborEdgeStatus();
    } catch (err) {
      const data = err.response?.data;
      setLaborEdgeResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setLaborEdgeError(data?.error?.message || data?.error || 'Failed to refresh LaborEdge token');
      await fetchLaborEdgeStatus();
    } finally {
      setLaborEdgeActionLoading(false);
    }
  };

  const bootstrapLaborEdgeAuth = async () => {
    setLaborEdgeActionLoading(true);
    setLaborEdgeError('');

    try {
      const res = await api.post('/admin/laboredge/bootstrap-auth');
      setLaborEdgeResult({
        ...(res.data || {}),
        returnStatusCode: res.status,
      });
      await fetchLaborEdgeStatus();
    } catch (err) {
      const data = err.response?.data;
      setLaborEdgeResult({
        ...(data || {}),
        returnStatusCode: err.response?.status || null,
      });
      setLaborEdgeError(data?.error?.message || data?.error || 'Failed to bootstrap LaborEdge authentication');
      await fetchLaborEdgeStatus();
    } finally {
      setLaborEdgeActionLoading(false);
    }
  };

  const handleLaborEdgePageChange = async (nextPage) => {
    if (laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null) {
      return;
    }

    await executeLaborEdgeRequest({
      endpoint: '/admin/laboredge/jobs/cached',
      page: nextPage,
      pageSize: laborEdgePageSize,
      failureMessage: 'Failed to load cached LaborEdge jobs',
    });
  };

  const handleLaborEdgePageSizeChange = async (value) => {
    if (laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null) {
      return;
    }

    const nextPageSize = Number(value) || 25;

    await executeLaborEdgeRequest({
      endpoint: '/admin/laboredge/jobs/cached',
      page: 1,
      pageSize: nextPageSize,
      failureMessage: 'Failed to load cached LaborEdge jobs',
    });
  };

  const handleZohoPageChange = async (nextPage) => {
    if (zohoActionLoading) {
      return;
    }

    await executeZohoRequest({
      endpoint: '/admin/zoho/jobs/cached',
      page: nextPage,
      pageSize: zohoPageSize,
      failureMessage: 'Failed to load cached Zoho jobs',
    });
  };

  const handleZohoPageSizeChange = async (value) => {
    const nextPageSize = Number(value) || 25;

    await executeZohoRequest({
      endpoint: '/admin/zoho/jobs/cached',
      page: 1,
      pageSize: nextPageSize,
      failureMessage: 'Failed to load cached Zoho jobs',
    });
  };

  const runJobDivaCallApi = async () => {
    await executeJobDivaRequest({
      endpoint: '/admin/jobdiva/call-api',
      page: 1,
      pageSize: jobDivaPageSize,
      action: 'call-api',
      failureMessage: 'JobDiva API call failed',
    });
  };

  const applyJobDivaFilters = async () => {
    setJobDivaFiltersAppliedAt(new Date());
    await executeJobDivaRequest({
      endpoint: '/admin/jobdiva/jobs/cached',
      page: 1,
      pageSize: jobDivaPageSize,
      action: 'call-api',
      failureMessage: 'Failed to load cached JobDiva jobs',
    });
  };

  const runJobDivaCleanup = async () => {
    setJobDivaCleanupProgress(null);
    await executeJobDivaRequest({
      endpoint: '/admin/jobdiva/cleanup/run',
      page: 1,
      pageSize: jobDivaPageSize,
      action: 'cleanup',
      failureMessage: 'Failed to clean JobDiva descriptions',
    });
  };

  const resetJobDivaCleanups = async () => {
    const confirmed = window.confirm(
      'This will delete all rows from JobDivaDescriptionCleanups. Cached JobDiva jobs will remain. Continue?'
    );

    if (!confirmed) {
      return;
    }

    await executeJobDivaRequest({
      endpoint: '/admin/jobdiva/cleanup/reset',
      page: 1,
      pageSize: jobDivaPageSize,
      action: 'reset',
      failureMessage: 'Failed to reset JobDiva cleanup table',
    });
  };

  const handleJobDivaPageChange = async (nextPage) => {
    if (jobDivaTestLoading || jobDivaCleanupLoading || jobDivaResetLoading) {
      return;
    }

    try {
      await executeJobDivaRequest({
        endpoint: '/admin/jobdiva/jobs/cached',
        page: nextPage,
        pageSize: jobDivaPageSize,
        action: 'call-api',
        failureMessage: 'Failed to load cached JobDiva jobs',
      });
    } catch (_error) {
      setJobDivaError('Unexpected pagination error while loading JobDiva data.');
    }
  };

  const handleJobDivaPageSizeChange = async (value) => {
    const nextPageSize = Number(value) || 25;

    try {
      await executeJobDivaRequest({
        endpoint: '/admin/jobdiva/jobs/cached',
        page: 1,
        pageSize: nextPageSize,
        action: 'call-api',
        failureMessage: 'Failed to load cached JobDiva jobs',
      });
    } catch (_error) {
      setJobDivaError('Unexpected pagination error while changing page size.');
    }
  };

  const handleJobDivaReviewNoteChange = (cleanupId, value) => {
    setJobDivaReviewNotes((prev) => ({
      ...prev,
      [cleanupId]: value,
    }));
  };

  const handleJobDivaCleanupReview = async (cleanupId, approvalStatus) => {
    setJobDivaReviewActionId(cleanupId);
    setJobDivaFlaggedError('');

    try {
      await api.patch(`/admin/jobdiva/cleanup/${cleanupId}/review`, {
        approvalStatus,
        reviewReason: String(jobDivaReviewNotes[cleanupId] || '').trim(),
      });
      await fetchJobDivaFlaggedItems();
    } catch (err) {
      setJobDivaFlaggedError(err.response?.data?.error || 'Failed to update JobDiva cleanup review');
    } finally {
      setJobDivaReviewActionId(null);
    }
  };

  const formatJobDivaTimestamp = (value) => {
    if (!value) return '—';

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return String(value);
    }

    return parsed.toLocaleString();
  };

  const getJobDivaSourceDescription = (job) => {
    const raw = job?.raw || {};
    const candidates = [
      raw['job description'],
      raw['posting description'],
      raw.Description,
      raw.description,
      raw.JobDescription,
      raw.POSTINGDESCRIPTION,
      raw.JOBDESCRIPTION,
    ];

    const directMatch = candidates.find((candidate) => String(candidate || '').trim());
    if (directMatch) {
      return String(directMatch).trim();
    }

    // Final fallback: any raw JobDiva field that looks like a description key.
    const discoveredRawDescription = Object.entries(raw).find(([key, value]) => (
      /description/i.test(String(key || '')) && String(value || '').trim()
    ));

    const match = discoveredRawDescription?.[1] || job?.descriptionCleanup?.sourceDescription;
    return String(match || '').trim();
  };

  const openJobDivaDescriptionModal = (job) => {
    setSelectedJobDivaPreviewJob(job || null);
  };

  const closeJobDivaDescriptionModal = () => {
    setSelectedJobDivaPreviewJob(null);
  };

  const getZohoJobDescription = (job) => {
    const raw = job?.raw || {};
    const candidates = [
      raw.Job_Description,
      raw.job_description,
      raw.Description,
      raw.description,
      raw.Summary,
      job?.description,
      job?.summary,
    ];

    const match = candidates.find((candidate) => String(candidate || '').trim());
    return String(match || '').trim();
  };

  const openZohoDescriptionModal = (job) => {
    setSelectedZohoPreviewJob(job || null);
  };

  const closeZohoDescriptionModal = () => {
    setSelectedZohoPreviewJob(null);
  };

  const decodeLaborEdgeHtmlEntities = (value) => String(value || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");

  const stripLaborEdgeHtml = (value) => decodeLaborEdgeHtmlEntities(String(value || ''))
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const escapePreviewHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const formatLaborEdgeDescriptionForPreview = (value) => {
    const normalizedText = stripLaborEdgeHtml(value);
    if (!normalizedText) return '';

    const lines = normalizedText
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .split('\n');

    const htmlChunks = [];
    let paragraphLines = [];
    let inList = false;

    const flushParagraph = () => {
      if (paragraphLines.length === 0) return;
      htmlChunks.push(`<p>${paragraphLines.map((line) => escapePreviewHtml(line)).join('<br />')}</p>`);
      paragraphLines = [];
    };

    for (const rawLine of lines) {
      const line = String(rawLine || '').trim();

      if (!line) {
        flushParagraph();
        if (inList) {
          htmlChunks.push('</ul>');
          inList = false;
        }
        continue;
      }

      const bulletMatch = line.match(/^[-*\u2022\u00B7]\s*(.+)$/);
      if (bulletMatch) {
        flushParagraph();
        if (!inList) {
          htmlChunks.push('<ul>');
          inList = true;
        }
        htmlChunks.push(`<li>${escapePreviewHtml(bulletMatch[1])}</li>`);
        continue;
      }

      if (inList) {
        htmlChunks.push('</ul>');
        inList = false;
      }

      paragraphLines.push(line);
    }

    flushParagraph();
    if (inList) {
      htmlChunks.push('</ul>');
    }

    return htmlChunks.join('');
  };

  const getLaborEdgeJobDescription = (job) => {
    const raw = job?.raw || {};
    const candidates = [
      job?.description,
      job?.summary,
      raw.description,
      raw.jobDescription,
      raw.Description,
      raw.Summary,
    ];

    const match = candidates.find((candidate) => String(candidate || '').trim());
    return String(match || '').trim();
  };

  const getLaborEdgeRawDiagnosticValue = (job, keys, fallback = '—') => {
    const raw = job?.raw || {};

    for (const key of keys) {
      if (raw?.[key] != null && String(raw[key]).trim() !== '') {
        return String(raw[key]);
      }
    }

    return fallback;
  };

  const openLaborEdgeDescriptionModal = (job) => {
    setSelectedLaborEdgePreviewJob(job || null);
  };

  const closeLaborEdgeDescriptionModal = () => {
    setSelectedLaborEdgePreviewJob(null);
  };

  const fetchIndeedRssStatus = async () => {
    setIndeedRssStatusLoading(true);
    setIndeedRssError('');
    try {
      const res = await api.get('/api/admin/indeed-rss/status');
      setIndeedRssStatus(res.data);
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to load Indeed RSS status';
      setIndeedRssError(message);
      setIndeedRssStatus(null);
    } finally {
      setIndeedRssStatusLoading(false);
    }
  };

  const executeIndeedRssRequest = async (endpoint, payload = {}) => {
    setIndeedRssActionLoading(true);
    setIndeedRssError('');
    setIndeedRssResult(null);
    try {
      const res = await api.post(endpoint, payload);
      setIndeedRssResult(res.data);
      if (res.data.ok) {
        setTimeout(() => fetchIndeedRssStatus(), 500);
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.error?.message || err?.message || 'API request failed';
      setIndeedRssError(errorMsg);
      setIndeedRssResult(null);
    } finally {
      setIndeedRssActionLoading(false);
    }
  };

  const runIndeedRssSync = () => {
    executeIndeedRssRequest('/api/admin/indeed-rss/sync');
  };

  const applyIndeedRssFilters = async () => {
    setIndeedRssPage(1);
    setIndeedRssFiltersAppliedAt(new Date());
    await executeIndeedRssRequest('/api/admin/indeed-rss/jobs/cached', {
      keyword: indeedRssForm.keyword,
      location: indeedRssForm.location,
      page: 1,
      pageSize: indeedRssPageSize,
    });
  };

  const handleIndeedRssPageChange = (newPage) => {
    setIndeedRssPage(newPage);
    executeIndeedRssRequest('/api/admin/indeed-rss/jobs/cached', {
      keyword: indeedRssForm.keyword,
      location: indeedRssForm.location,
      page: newPage,
      pageSize: indeedRssPageSize,
    });
  };

  const handleIndeedRssPageSizeChange = (newSize) => {
    setIndeedRssPageSize(newSize);
    setIndeedRssPage(1);
    executeIndeedRssRequest('/api/admin/indeed-rss/jobs/cached', {
      keyword: indeedRssForm.keyword,
      location: indeedRssForm.location,
      page: 1,
      pageSize: newSize,
    });
  };

  const handleIndeedRssFormChange = (field, value) => {
    setIndeedRssForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const getIndeedRssJobDescription = (job) => {
    return String(job?.description || '').trim();
  };

  const openIndeedRssDescriptionModal = (job) => {
    setSelectedIndeedRssPreviewJob(job || null);
  };

  const closeIndeedRssDescriptionModal = () => {
    setSelectedIndeedRssPreviewJob(null);
  };

  const executeKillEmailWorker = async () => {
    setKillingEmailWorker(true);
    setEmailWorkerActionError('');
    setEmailWorkerActionSuccess('');

    try {
      await api.post('/admin/email-worker/kill');
      await fetchEmailWorkerStatus();
      setEmailWorkerActionSuccess('Email worker stopped.');
      setConfirmAction({ show: false, type: null });
    } catch (err) {
      setEmailWorkerActionError(err.response?.data?.error || 'Failed to stop email worker');
    } finally {
      setKillingEmailWorker(false);
    }
  };

  const handleKillEmailWorker = () => {
    setConfirmAction({ show: true, type: 'kill-worker' });
  };

  const handleStartEmailWorker = async () => {
    setStartingEmailWorker(true);
    setEmailWorkerActionError('');
    setEmailWorkerActionSuccess('');

    try {
      await api.post('/admin/email-worker/start');
      await fetchEmailWorkerStatus();
      setEmailWorkerActionSuccess('Email worker started.');
    } catch (err) {
      setEmailWorkerActionError(err.response?.data?.error || 'Failed to start email worker');
    } finally {
      setStartingEmailWorker(false);
    }
  };

  const executeClearQueue = async () => {
    setClearingQueue(true);
    setEmailWorkerActionError('');
    setEmailWorkerActionSuccess('');

    try {
      await api.delete('/queue/clear');
      await Promise.all([fetchEmailWorkerStatus(), fetchEmailDiagnostics()]);
      setEmailWorkerActionSuccess('Queue cleared successfully.');
      setConfirmAction({ show: false, type: null });
    } catch (err) {
      setEmailWorkerActionError(err.response?.data?.error || 'Failed to clear queue');
    } finally {
      setClearingQueue(false);
    }
  };

  const handleClearQueue = () => {
    setConfirmAction({ show: true, type: 'clear-queue' });
  };

  const handleEmailDiagnosticsFilterChange = (key, value) => {
    setEmailDiagnosticFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyEmailDiagnosticsFilters = () => {
    fetchEmailDiagnostics();
  };

  const handleRefreshEmailDiagnostics = () => {
    setEmailWorkerActionSuccess('');
    setEmailWorkerActionError('');
    fetchEmailDiagnostics();
    fetchEmailWorkerStatus();
  };

  const handleRefreshRsvpDiagnostics = () => {
    fetchRsvpAccessDiagnostics();
  };

  const handleClearEmailDiagnosticsFilters = () => {
    const clearedFilters = { recipient: '', status: '', sender: '' };
    setEmailDiagnosticFilters(clearedFilters);
    fetchEmailDiagnostics(clearedFilters);
  };

  const handleRsvpAccessDiagnosticsFilterChange = (key, value) => {
    setRsvpAccessDiagnosticFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyRsvpAccessDiagnosticsFilters = () => {
    fetchRsvpAccessDiagnostics();
  };

  const handleClearRsvpAccessDiagnosticsFilters = () => {
    const clearedFilters = {
      outcome: '',
      action: '',
      eventId: '',
      likelyScanner: '',
      tokenFingerprint: '',
      recipientEmail: '',
    };
    setRsvpAccessDiagnosticFilters(clearedFilters);
    setRsvpRecipientSearchTerm('');
    setShowRsvpRecipientSuggestions(false);
    fetchRsvpAccessDiagnostics(clearedFilters);
  };

  const handleRoleToggle = (userId, role) => {
    const currentRoles = roleUpdates[userId] || (users.find(u => u.UserId === userId)?.Role || '').split(',').filter(r => r.trim());
    const updated = currentRoles.includes(role)
      ? currentRoles.filter(r => r !== role)
      : [...currentRoles, role];
    setRoleUpdates((prev) => ({ ...prev, [userId]: updated }));
  };

  const saveRole = async (userId) => {
    try {
      const roles = roleUpdates[userId] || [];
      await api.put(`/admin/users/${userId}/role`, { role: roles.join(',') });
      setOpenDropdown(null);
      await fetchUsers();
    } catch (err) {
      alert('Failed to update roles: ' + (err.response?.data?.error || err.message));
    }
  };

  const getUserRoles = (userId) => {
    const override = roleUpdates[userId];
    if (override) return override;
    const user = users.find(u => u.UserId === userId);
    return (user?.Role || '').split(',').filter(r => r.trim());
  };

  const isProtectedUser = (u) => (
    String(u?.Role || '').toLowerCase() === 'superuser' ||
    String(u?.Email || '').toLowerCase() === 'john.robinson@rsc.com'
  );

  const requestDeactivateUser = (targetUser) => {
    setUserStatusError('');
    setPendingStatusUser({ ...targetUser, targetActive: false, needsConfirmedRetry: false });
  };

  const requestReactivateUser = (targetUser) => {
    setUserStatusError('');
    setPendingStatusUser({ ...targetUser, targetActive: true, needsConfirmedRetry: false });
  };

  const executeStatusChange = async () => {
    if (!pendingStatusUser) return;
    setUserStatusUpdatingId(pendingStatusUser.UserId);
    setUserStatusError('');
    try {
      await api.put(`/admin/users/${pendingStatusUser.UserId}/status`, {
        isActive: pendingStatusUser.targetActive,
        confirmed: pendingStatusUser.needsConfirmedRetry
      });
      setPendingStatusUser(null);
      await fetchUsers();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.warning === 'stillActiveInAAD') {
        setPendingStatusUser((prev) => (prev ? { ...prev, needsConfirmedRetry: true } : prev));
      } else {
        setUserStatusError(err.response?.data?.error || 'Failed to update user status');
      }
    } finally {
      setUserStatusUpdatingId(null);
    }
  };

  const toggleRoleFilter = (role) => {
    setRoleFilters((prev) => (
      prev.includes(role)
        ? prev.filter((item) => item !== role)
        : [...prev, role]
    ));
  };

  const roleFilterOptions = useMemo(() => {
    const roleSet = new Set();
    users.forEach((u) => {
      (u?.Role || '')
        .split(',')
        .map(r => r.trim())
        .filter(Boolean)
        .forEach(role => roleSet.add(role));
    });
    return Array.from(roleSet).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const normalizedSearch = userSearchTerm.trim().toLowerCase();

    return users.filter((u) => {
      if (!showInactiveUsers && u.IsActive === false) return false;

      const displayName = String(u.DisplayName || '').toLowerCase();
      const email = String(u.Email || '').toLowerCase();
      const currentRoles = getUserRoles(u.UserId);

      const matchesSearch = !normalizedSearch ||
        displayName.includes(normalizedSearch) ||
        email.includes(normalizedSearch);

      const matchesRole = roleFilters.length === 0 || roleFilters.some((role) => currentRoles.includes(role));

      return matchesSearch && matchesRole;
    });
  }, [users, roleUpdates, userSearchTerm, roleFilters, showInactiveUsers]);

  const canWriteJobDivaUsers = hasPermission('admin:users:write');

  const fetchJobDivaUsers = async ({ refresh = false } = {}) => {
    setJobDivaUsersLoading(true);
    setJobDivaUsersError('');
    try {
      const [usersRes, divisionsRes] = await Promise.all([
        api.get(`/admin/jobdiva/users${refresh ? '?refresh=true' : ''}`),
        api.get('/admin/jobdiva/divisions'),
      ]);
      setJobDivaUsers(Array.isArray(usersRes.data?.users) ? usersRes.data.users : []);
      setJobDivaAssignableDivisions(Array.isArray(divisionsRes.data?.assignable) ? divisionsRes.data.assignable : []);
    } catch (err) {
      setJobDivaUsersError(err.response?.data?.error?.message || 'Failed to load JobDiva users.');
    } finally {
      setJobDivaUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'jobdiva-diagnostics' || jobDivaSubTab !== 'users' || !canRunJobDivaDiagnostics) {
      return;
    }
    fetchJobDivaUsers();
  }, [activeTab, jobDivaSubTab, canRunJobDivaDiagnostics]);

  const jobDivaUserDivisionOptions = useMemo(() => (
    Array.from(new Set(jobDivaUsers.map((u) => u.divisionName).filter(Boolean))).sort((a, b) => a.localeCompare(b))
  ), [jobDivaUsers]);

  const filteredJobDivaUsers = useMemo(() => {
    const search = jobDivaUserFilters.search.trim().toLowerCase();
    return jobDivaUsers.filter((u) => {
      if (!jobDivaUserFilters.showInactive && !u.active) return false;
      if (jobDivaUserFilters.division === '__none__' && u.divisionName) return false;
      if (jobDivaUserFilters.division && jobDivaUserFilters.division !== '__none__' && u.divisionName !== jobDivaUserFilters.division) return false;
      if (!search) return true;
      return `${u.firstName} ${u.lastName}`.toLowerCase().includes(search)
        || u.email.toLowerCase().includes(search)
        || u.userId.includes(search);
    });
  }, [jobDivaUsers, jobDivaUserFilters]);

  const allFilteredJobDivaUsersSelected = filteredJobDivaUsers.length > 0
    && filteredJobDivaUsers.every((u) => jobDivaSelectedUserIds.has(u.userId));

  const toggleJobDivaUserSelected = (userId) => {
    setJobDivaSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const toggleAllFilteredJobDivaUsers = () => {
    setJobDivaSelectedUserIds((prev) => {
      const next = new Set(prev);
      filteredJobDivaUsers.forEach((u) => {
        if (allFilteredJobDivaUsersSelected) next.delete(u.userId); else next.add(u.userId);
      });
      return next;
    });
  };

  const saveJobDivaUserDivision = async (jobDivaUser) => {
    const division = jobDivaRowDivisions[jobDivaUser.userId];
    if (!division) return;

    setJobDivaUserSavingId(jobDivaUser.userId);
    setJobDivaUsersError('');
    setJobDivaUsersSuccess('');
    try {
      const res = await api.put(`/admin/jobdiva/users/${encodeURIComponent(jobDivaUser.userId)}/division`, { division });
      const result = res.data?.result;
      setJobDivaUsersSuccess(result?.status === 'unchanged'
        ? `${result.name || jobDivaUser.userId} is already in ${division}.`
        : `${result?.name || jobDivaUser.userId} moved to ${division}.`);
      setJobDivaRowDivisions((prev) => ({ ...prev, [jobDivaUser.userId]: '' }));
      await fetchJobDivaUsers({ refresh: true });
    } catch (err) {
      setJobDivaUsersError(err.response?.data?.error?.message || 'Failed to update JobDiva user division.');
    } finally {
      setJobDivaUserSavingId(null);
    }
  };

  const runJobDivaBulkDivisionUpdate = async () => {
    setJobDivaBulkConfirmOpen(false);
    setJobDivaBulkRunning(true);
    setJobDivaUsersError('');
    setJobDivaUsersSuccess('');
    setJobDivaBulkResult(null);
    try {
      const res = await api.post('/admin/jobdiva/users/division/bulk', {
        userIds: Array.from(jobDivaSelectedUserIds),
        division: jobDivaBulkDivision,
      });
      setJobDivaBulkResult(res.data || null);
      const failedIds = new Set((res.data?.results || []).filter((r) => !r.ok).map((r) => r.userId));
      setJobDivaSelectedUserIds(failedIds);
      await fetchJobDivaUsers({ refresh: true });
    } catch (err) {
      setJobDivaUsersError(err.response?.data?.error?.message || 'Bulk division update failed.');
    } finally {
      setJobDivaBulkRunning(false);
    }
  };

  if (!user || !canAccessAdmin) {
    return <div className="container"><h2>Access Denied</h2><p>You do not have permission to view this page.</p></div>;
  }

  return (
    <div className="container">
      <h1>Admin Panel</h1>
      
      <div className="admin-tabs">
        {canManageUsers && (
          <button 
            className={`admin-tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            User Roles
          </button>
        )}
        {canManagePermissions && (
          <button 
            className={`admin-tab-btn ${activeTab === 'permissions' ? 'active' : ''}`}
            onClick={() => setActiveTab('permissions')}
          >
            Tab Permissions
          </button>
        )}
        {canOperateEmailWorker && (
          <>
            <button
              className={`admin-tab-btn ${activeTab === 'email-diagnostics' ? 'active' : ''}`}
              onClick={() => setActiveTab('email-diagnostics')}
            >
              Email Diagnostics
            </button>
            <button
              className={`admin-tab-btn ${activeTab === 'rsvp-diagnostics' ? 'active' : ''}`}
              onClick={() => setActiveTab('rsvp-diagnostics')}
            >
              RSVP Diagnostics
            </button>
          </>
        )}
        {canRunJobDivaDiagnostics && (
          <button
            className={`admin-tab-btn ${activeTab === 'jobdiva-diagnostics' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobdiva-diagnostics')}
          >
            JobDiva Diagnostics
          </button>
        )}
        {canRunZohoDiagnostics && (
          <button
            className={`admin-tab-btn ${activeTab === 'zoho-diagnostics' ? 'active' : ''}`}
            onClick={() => setActiveTab('zoho-diagnostics')}
          >
            Zoho Diagnostics
          </button>
        )}
        {canRunZohoDiagnostics && (
          <button
            type="button"
            className={`admin-tab-btn ${activeTab === 'zoho-jobdiva-etl' ? 'active' : ''}`}
            onClick={() => setActiveTab('zoho-jobdiva-etl')}
          >
            Zoho → JobDiva ETL
          </button>
        )}
        {canRunZohoDiagnostics && (
          <button
            className={`admin-tab-btn ${activeTab === 'zoho-maintenance' ? 'active' : ''}`}
            onClick={() => setActiveTab('zoho-maintenance')}
          >
            SDP Maintenance
          </button>
        )}
        {canRunLaborEdgeDiagnostics && (
          <button
            className={`admin-tab-btn ${activeTab === 'laboredge-diagnostics' ? 'active' : ''}`}
            onClick={() => setActiveTab('laboredge-diagnostics')}
          >
            LaborEdge Diagnostics
          </button>
        )}
        {canRunIndeedRssDiagnostics && (
          <button
            className={`admin-tab-btn ${activeTab === 'indeed-rss-diagnostics' ? 'active' : ''}`}
            onClick={() => setActiveTab('indeed-rss-diagnostics')}
          >
            Indeed RSS Diagnostics
          </button>
        )}
      </div>

      {activeTab === 'users' && canManageUsers && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <h2 style={{ margin: 0 }}>User Role Management</h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn btn-secondary" onClick={() => setShowImportUsersModal(true)}>
                Import from Azure AD
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setShowAddUserModal(true)}>
                Add User
              </button>
            </div>
          </div>

          <div className="admin-user-filters">
            <input
              type="text"
              className="admin-user-search"
              placeholder="Search by name or email"
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={showInactiveUsers}
                onChange={(e) => setShowInactiveUsers(e.target.checked)}
              />
              Show inactive users
            </label>
            <div className="admin-role-filter-wrap" data-role-filter-dropdown>
              <button
                type="button"
                className="admin-role-filter"
                onClick={() => setOpenRoleFilterDropdown((prev) => !prev)}
              >
                {roleFilters.length === 0
                  ? 'All current roles'
                  : `${roleFilters.length} role(s) selected`}
                <span>▼</span>
              </button>
              {openRoleFilterDropdown && (
                <div className="admin-role-filter-menu">
                  {roleFilterOptions.map((role) => (
                    <label key={role} className="admin-role-filter-option">
                      <input
                        type="checkbox"
                        checked={roleFilters.includes(role)}
                        onChange={() => toggleRoleFilter(role)}
                      />
                      <span>{role}</span>
                    </label>
                  ))}
                  <div className="admin-role-filter-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setRoleFilters([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Existing user management */}
          {userStatusError && <div style={{ color: 'red', marginTop: '8px' }}>{userStatusError}</div>}
          {loading ? (
            <div>Loading users...</div>
          ) : error ? (
            <div style={{ color: 'red' }}>{error}</div>
          ) : filteredUsers.length === 0 ? (
            <div>No users match the current search/filter.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Display Name</th>
                  <th>Status</th>
                  <th>Current Roles</th>
                  <th>Assign Roles</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.UserId}>
                    <td>{u.Email}</td>
                    <td>{u.DisplayName}</td>
                    <td>
                      <span className={`admin-status-badge ${u.IsActive === false ? 'inactive' : 'active'}`}>
                        {u.IsActive === false ? 'Inactive' : 'Active'}
                      </span>
                      {!isProtectedUser(u) && u.UserId !== (user?.userId || user?.UserId || user?.id) && (
                        u.IsActive === false ? (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ marginLeft: '8px' }}
                            disabled={userStatusUpdatingId === u.UserId}
                            onClick={() => requestReactivateUser(u)}
                          >
                            Reactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="admin-deactivate-icon-btn"
                            title="Deactivate user"
                            aria-label="Deactivate user"
                            disabled={userStatusUpdatingId === u.UserId}
                            onClick={() => requestDeactivateUser(u)}
                          >
                            ⊘
                          </button>
                        )
                      )}
                    </td>
                    <td>{getUserRoles(u.UserId).join(', ') || '(none)'}</td>
                    <td>
                      <div style={{ position: 'relative' }} data-role-dropdown>
                        <button
                          onClick={() => setOpenDropdown(openDropdown === u.UserId ? null : u.UserId)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: '14px'
                          }}
                          disabled={u.Role === 'superuser' || u.Email?.toLowerCase() === 'john.robinson@rsc.com'}
                        >
                          {getUserRoles(u.UserId).length > 0 
                            ? `${getUserRoles(u.UserId).length} role(s) selected`
                            : 'Select roles...'}
                          <span style={{ float: 'right' }}>▼</span>
                        </button>
                        
                        {openDropdown === u.UserId && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            zIndex: 1000,
                            marginTop: '4px'
                          }}>
                            {availableRoles.map(role => (
                              <label key={role} className="admin-assign-role-option">
                                <input
                                  className="admin-assign-role-checkbox"
                                  type="checkbox"
                                  checked={getUserRoles(u.UserId).includes(role)}
                                  onChange={() => handleRoleToggle(u.UserId, role)}
                                />
                                <span>{role}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => saveRole(u.UserId)}
                        disabled={u.Role === 'superuser' || u.Email?.toLowerCase() === 'john.robinson@rsc.com'}
                      >
                        Save
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <AddUserModal
            show={showAddUserModal}
            onClose={() => setShowAddUserModal(false)}
            onUserAdded={fetchUsers}
          />
          <ImportUsersModal
            show={showImportUsersModal}
            onClose={() => setShowImportUsersModal(false)}
            onImportComplete={fetchUsers}
          />
          <ConfirmModal
            show={!!pendingStatusUser}
            title={pendingStatusUser?.targetActive ? 'Reactivate User' : 'Deactivate User'}
            message={
              pendingStatusUser?.targetActive
                ? `Reactivate ${pendingStatusUser?.DisplayName || pendingStatusUser?.Email}? They will regain access immediately.`
                : pendingStatusUser?.needsConfirmedRetry
                  ? `${pendingStatusUser?.DisplayName || pendingStatusUser?.Email} is still active/enabled in Azure AD. Deactivate anyway?`
                  : `Deactivate ${pendingStatusUser?.DisplayName || pendingStatusUser?.Email}? They will lose access to the app, but their campaigns/templates/contacts are kept.`
            }
            onConfirm={executeStatusChange}
            onCancel={() => setPendingStatusUser(null)}
            confirmText={pendingStatusUser?.targetActive ? 'Reactivate' : 'Deactivate'}
            confirmStyle={pendingStatusUser?.targetActive ? 'primary' : 'danger'}
          />
        </>
      )}

      {activeTab === 'permissions' && canManagePermissions && (
        <RoleManagement />
      )}

      {activeTab === 'email-diagnostics' && canOperateEmailWorker && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ margin: 0 }}>Email Diagnostics</h2>
            <button
              onClick={handleRefreshEmailDiagnostics}
              disabled={loadingEmailDiagnostics || loadingEmailWorkerStatus}
            >
              {loadingEmailDiagnostics ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div className="admin-email-worker-card">
            <div className="admin-email-worker-row">
              <strong>Email Worker</strong>
              <div className="admin-email-worker-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleClearQueue}
                  disabled={clearingQueue}
                >
                  {clearingQueue ? 'Clearing...' : 'Clear Queue'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleStartEmailWorker}
                  disabled={startingEmailWorker || clearingQueue || !!emailWorkerStatus?.isRunning}
                >
                  {startingEmailWorker ? 'Starting...' : 'Start Worker'}
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleKillEmailWorker}
                  disabled={killingEmailWorker || clearingQueue || !emailWorkerStatus?.isRunning}
                >
                  {killingEmailWorker ? 'Stopping...' : 'Kill Worker'}
                </button>
              </div>
            </div>
            {loadingEmailWorkerStatus ? (
              <div>Loading worker status...</div>
            ) : emailWorkerStatus ? (
              <div className="admin-email-worker-grid">
                <div><strong>Running:</strong> {emailWorkerStatus.isRunning ? 'Yes' : 'No'}</div>
                <div><strong>Approx Queue Messages:</strong> {Number.isFinite(emailWorkerStatus.approximateMessagesCount) ? emailWorkerStatus.approximateMessagesCount : 'N/A'}</div>
                <div><strong>Poll Interval:</strong> {emailWorkerStatus.pollIntervalMs ? `${emailWorkerStatus.pollIntervalMs} ms` : 'N/A'}</div>
                <div><strong>Batch Size:</strong> {emailWorkerStatus.batchSize || 'N/A'}</div>
              </div>
            ) : (
              <div>No worker status available.</div>
            )}
            {emailWorkerActionError && <div style={{ color: 'red', marginTop: 8 }}>{emailWorkerActionError}</div>}
            {emailWorkerActionSuccess && <div style={{ color: '#107c10', marginTop: 8 }}>{emailWorkerActionSuccess}</div>}
          </div>

          <div className="admin-email-diagnostic-filters">
            <input
              type="text"
              placeholder="Filter recipient"
              value={emailDiagnosticFilters.recipient}
              onChange={(e) => handleEmailDiagnosticsFilterChange('recipient', e.target.value)}
            />
            <select
              value={emailDiagnosticFilters.status}
              onChange={(e) => handleEmailDiagnosticsFilterChange('status', e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="queued">queued</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
              <option value="pending">pending</option>
            </select>
            <input
              type="text"
              placeholder="Filter sender"
              value={emailDiagnosticFilters.sender}
              onChange={(e) => handleEmailDiagnosticsFilterChange('sender', e.target.value)}
            />
            <button type="button" onClick={handleApplyEmailDiagnosticsFilters} disabled={loadingEmailDiagnostics}>
              Apply
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleClearEmailDiagnosticsFilters} disabled={loadingEmailDiagnostics}>
              Clear
            </button>
          </div>

          {loadingEmailDiagnostics ? (
            <div>Loading diagnostics...</div>
          ) : emailDiagnosticsError ? (
            <div style={{ color: 'red' }}>{emailDiagnosticsError}</div>
          ) : emailDiagnostics.length === 0 ? (
            <div>No email diagnostics logged yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Source</th>
                    <th>Event / Campaign</th>
                    <th>Owner</th>
                    <th>Type</th>
                    <th>Recipient</th>
                    <th>Status</th>
                    <th>Method</th>
                    <th>Sender</th>
                    <th>Opened</th>
                    <th>Clicked</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {emailDiagnostics.map((row) => (
                    <tr key={row.LogId}>
                      <td>{new Date(row.CreatedAt).toLocaleString()}</td>
                      <td>
                        {row.Source === 'campaign' ? 'Campaign' : row.Source === 'ncf' ? 'NCF' : 'RSVP'}
                      </td>
                      <td>
                        {row.Source === 'campaign'
                          ? (row.CampaignName || `Campaign #${row.CampaignId}`)
                          : (row.EventTitle || `Event #${row.EventId}`)}
                      </td>
                      <td>{row.OwnerEmail || '—'}</td>
                      <td>{row.NotificationType}</td>
                      <td>{row.RecipientEmail || '—'}</td>
                      <td>
                        <span style={{ color: row.DeliveryStatus === 'sent' ? '#107c10' : '#a4262c', fontWeight: 600 }}>
                          {row.DeliveryStatus}
                        </span>
                      </td>
                      <td>{row.DeliveryMethod || '—'}</td>
                      <td>{row.SenderMailbox || '—'}</td>
                      <td>{row.OpenedAt ? new Date(row.OpenedAt).toLocaleString() : (row.Opened ? 'Yes' : '—')}</td>
                      <td>{row.ClickedAt ? new Date(row.ClickedAt).toLocaleString() : (row.Clicked ? 'Yes' : '—')}</td>
                      <td style={{ maxWidth: '320px', whiteSpace: 'normal' }}>{row.ErrorMessage || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </>
      )}

      {activeTab === 'rsvp-diagnostics' && canOperateEmailWorker && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ margin: 0 }}>RSVP Diagnostics</h2>
            <button
              onClick={handleRefreshRsvpDiagnostics}
              disabled={loadingRsvpAccessDiagnostics}
            >
              {loadingRsvpAccessDiagnostics ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>

          <div className="admin-rsvp-access-card" style={{ marginTop: 0 }}>
            <div className="admin-rsvp-access-filters">
              <select
                value={rsvpAccessDiagnosticFilters.outcome}
                onChange={(e) => handleRsvpAccessDiagnosticsFilterChange('outcome', e.target.value)}
              >
                <option value="">All outcomes</option>
                <option value="requested">requested</option>
                <option value="view_loaded">view_loaded</option>
                <option value="submit_requested">submit_requested</option>
                <option value="decline_requested">decline_requested</option>
                <option value="submitted">submitted</option>
                <option value="declined">declined</option>
                <option value="token_not_found">token_not_found</option>
                <option value="token_expired">token_expired</option>
                <option value="missing_email">missing_email</option>
                <option value="error">error</option>
              </select>
              <select
                value={rsvpAccessDiagnosticFilters.action}
                onChange={(e) => handleRsvpAccessDiagnosticsFilterChange('action', e.target.value)}
              >
                <option value="">All actions</option>
                <option value="rsvp-view">rsvp-view</option>
                <option value="rsvp-submit">rsvp-submit</option>
                <option value="rsvp-decline">rsvp-decline</option>
              </select>
              <input
                type="text"
                placeholder="Filter event ID"
                value={rsvpAccessDiagnosticFilters.eventId}
                onChange={(e) => handleRsvpAccessDiagnosticsFilterChange('eventId', e.target.value)}
              />
              <select
                value={rsvpAccessDiagnosticFilters.likelyScanner}
                onChange={(e) => handleRsvpAccessDiagnosticsFilterChange('likelyScanner', e.target.value)}
              >
                <option value="">Scanner: Any</option>
                <option value="true">Scanner: Likely</option>
                <option value="false">Scanner: Unlikely</option>
              </select>
              <input
                type="text"
                placeholder="Filter token fingerprint"
                value={rsvpAccessDiagnosticFilters.tokenFingerprint}
                onChange={(e) => handleRsvpAccessDiagnosticsFilterChange('tokenFingerprint', e.target.value)}
              />
              <div style={{ position: 'relative', minWidth: '180px' }}>
                <input
                  type="text"
                  placeholder="Filter recipient email"
                  value={rsvpRecipientSearchTerm}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRsvpRecipientSearchTerm(newValue);
                    if (newValue.trim()) {
                      fetchRsvpAccessRecipients(newValue);
                      setShowRsvpRecipientSuggestions(true);
                    } else {
                      setShowRsvpRecipientSuggestions(false);
                      setRsvpAccessRecipients([]);
                    }
                  }}
                  onFocus={() => {
                    if (rsvpRecipientSearchTerm.trim() || rsvpAccessRecipients.length > 0) {
                      setShowRsvpRecipientSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowRsvpRecipientSuggestions(false), 150);
                  }}
                />
                {showRsvpRecipientSuggestions && rsvpAccessRecipients.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000
                  }}>
                    {rsvpAccessRecipients.map((recipient) => (
                      <div
                        key={recipient}
                        onClick={() => {
                          handleRsvpAccessDiagnosticsFilterChange('recipientEmail', recipient);
                          setRsvpRecipientSearchTerm(recipient);
                          setShowRsvpRecipientSuggestions(false);
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          fontSize: '14px'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#f0f0f0';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = '#fff';
                        }}
                      >
                        {recipient}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button type="button" onClick={handleApplyRsvpAccessDiagnosticsFilters} disabled={loadingRsvpAccessDiagnostics}>
                Apply
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleClearRsvpAccessDiagnosticsFilters} disabled={loadingRsvpAccessDiagnostics}>
                Clear
              </button>
            </div>

            {loadingRsvpAccessDiagnostics ? (
              <div>Loading RSVP access diagnostics...</div>
            ) : rsvpAccessDiagnosticsError ? (
              <div style={{ color: 'red' }}>{rsvpAccessDiagnosticsError}</div>
            ) : rsvpAccessDiagnostics.length === 0 ? (
              <div>No RSVP access diagnostics logged yet.</div>
            ) : (
              <div className="admin-rsvp-access-table-wrap">
                <table className="admin-rsvp-access-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Action</th>
                      <th>Outcome</th>
                      <th>HTTP</th>
                      <th>Event</th>
                      <th>Recipient</th>
                      <th>Event Owner</th>
                      <th>Token Fingerprint</th>
                      <th>Scanner</th>
                      <th>IP</th>
                      <th>User Agent</th>
                      <th>Referrer</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rsvpAccessDiagnostics.map((row) => (
                      <tr key={row.AccessLogId}>
                        <td>{new Date(row.CreatedAt).toLocaleString()}</td>
                        <td>{row.Action || '—'}</td>
                        <td>
                          <span className={`admin-rsvp-outcome-badge ${String(row.Outcome || '').toLowerCase()}`}>
                            {row.Outcome || '—'}
                          </span>
                        </td>
                        <td>{row.HttpStatus || '—'}</td>
                        <td>{row.EventTitle || (row.EventId ? `Event #${row.EventId}` : '—')}</td>
                        <td>
                          {row.RecipientEmail ? (
                            <div style={{ fontSize: '13px' }}>
                              <div>{row.RecipientEmail}</div>
                              {(row.RecipientFirstName || row.RecipientLastName) && (
                                <div style={{ color: '#666', fontSize: '12px' }}>
                                  {[row.RecipientFirstName, row.RecipientLastName].filter(Boolean).join(' ')}
                                </div>
                              )}
                            </div>
                          ) : '—'}
                        </td>
                        <td>{row.OwnerEmail || '—'}</td>
                        <td>{row.TokenFingerprint || '—'}</td>
                        <td>{row.LikelyScanner ? 'Likely' : 'No'}</td>
                        <td>{row.IpAddress || '—'}</td>
                        <td>{row.UserAgent || '—'}</td>
                        <td>{row.Referrer || '—'}</td>
                        <td>{row.ErrorMessage || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
        </>
      )}

      {activeTab === 'jobdiva-diagnostics' && canRunJobDivaDiagnostics && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ margin: 0 }}>JobDiva Diagnostics</h2>
            <button
              onClick={() => {
                fetchJobDivaStatus();
                fetchJobDivaSchedule();
              }}
              disabled={jobDivaStatusLoading || jobDivaTestLoading || jobDivaCleanupLoading || jobDivaResetLoading || jobDivaScheduleLoading}
            >
              {jobDivaStatusLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
          </div>

          <div className="admin-tabs" style={{ marginBottom: '16px' }}>
            <button
              type="button"
              className={`admin-tab-btn ${jobDivaSubTab === 'jobs' ? 'active' : ''}`}
              onClick={() => setJobDivaSubTab('jobs')}
            >
              Jobs
            </button>
            <button
              type="button"
              className={`admin-tab-btn ${jobDivaSubTab === 'candidates' ? 'active' : ''}`}
              onClick={() => setJobDivaSubTab('candidates')}
            >
              Candidates
            </button>
            <button
              type="button"
              className={`admin-tab-btn ${jobDivaSubTab === 'users' ? 'active' : ''}`}
              onClick={() => setJobDivaSubTab('users')}
            >
              Users
            </button>
          </div>

          {jobDivaSubTab === 'users' && (
            <div className="admin-jobdiva-card">
              <div className="admin-jobdiva-review-header">
                <div>
                  <h3>JobDiva Users</h3>
                  <p className="admin-jobdiva-subtle">
                    Assign users to a division. Updates are written directly to JobDiva.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => fetchJobDivaUsers({ refresh: true })}
                  disabled={jobDivaUsersLoading || jobDivaBulkRunning}
                >
                  {jobDivaUsersLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              <div className="admin-jobdiva-filters">
                <label>
                  <span>Search</span>
                  <input
                    type="text"
                    value={jobDivaUserFilters.search}
                    onChange={(e) => setJobDivaUserFilters((prev) => ({ ...prev, search: e.target.value }))}
                    placeholder="Name, email or user ID"
                  />
                </label>
                <label>
                  <span>Division</span>
                  <select
                    value={jobDivaUserFilters.division}
                    onChange={(e) => setJobDivaUserFilters((prev) => ({ ...prev, division: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="__none__">No division</option>
                    {jobDivaUserDivisionOptions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Show inactive</span>
                  <input
                    type="checkbox"
                    checked={jobDivaUserFilters.showInactive}
                    onChange={(e) => setJobDivaUserFilters((prev) => ({ ...prev, showInactive: e.target.checked }))}
                  />
                </label>
              </div>

              {canWriteJobDivaUsers && (
                <div className="admin-jobdiva-table-toolbar" style={{ marginTop: 12 }}>
                  <strong>{jobDivaSelectedUserIds.size} selected</strong>
                  <select
                    value={jobDivaBulkDivision}
                    onChange={(e) => setJobDivaBulkDivision(e.target.value)}
                    disabled={jobDivaBulkRunning}
                  >
                    <option value="">Choose division...</option>
                    {jobDivaAssignableDivisions.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setJobDivaBulkConfirmOpen(true)}
                    disabled={jobDivaBulkRunning || !jobDivaBulkDivision || jobDivaSelectedUserIds.size === 0}
                  >
                    {jobDivaBulkRunning ? 'Updating...' : `Apply to ${jobDivaSelectedUserIds.size} selected`}
                  </button>
                  {jobDivaSelectedUserIds.size > 0 && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setJobDivaSelectedUserIds(new Set())}
                      disabled={jobDivaBulkRunning}
                    >
                      Clear selection
                    </button>
                  )}
                </div>
              )}

              {jobDivaUsersError && <div className="admin-jobdiva-inline-error">{jobDivaUsersError}</div>}
              {jobDivaUsersSuccess && <div style={{ color: '#166534', marginTop: 8 }}>{jobDivaUsersSuccess}</div>}

              {jobDivaBulkResult && (
                <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                  <div className={`admin-jobdiva-result-status ${jobDivaBulkResult.failed > 0 ? 'error' : 'ok'}`}>
                    {jobDivaBulkResult.division}: {jobDivaBulkResult.updated} updated, {jobDivaBulkResult.unchanged} already set, {jobDivaBulkResult.failed} failed
                  </div>
                  {jobDivaBulkResult.failed > 0 && (
                    <ul style={{ margin: '8px 0 0 16px' }}>
                      {(jobDivaBulkResult.results || []).filter((r) => !r.ok).map((r) => (
                        <li key={r.userId}><strong>{r.userId}</strong>: {r.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {jobDivaUsersLoading && jobDivaUsers.length === 0 ? (
                <div style={{ marginTop: 12 }}>Loading JobDiva users...</div>
              ) : filteredJobDivaUsers.length === 0 ? (
                <div style={{ marginTop: 12 }}>No JobDiva users match the current filters.</div>
              ) : (
                <div className="admin-jobdiva-table-wrap" style={{ marginTop: 12 }}>
                  <div style={{ marginBottom: 6 }}><strong>Showing:</strong> {filteredJobDivaUsers.length} of {jobDivaUsers.length}</div>
                  <table className="admin-jobdiva-table">
                    <thead>
                      <tr>
                        {canWriteJobDivaUsers && (
                          <th>
                            <input
                              type="checkbox"
                              checked={allFilteredJobDivaUsersSelected}
                              onChange={toggleAllFilteredJobDivaUsers}
                              disabled={jobDivaBulkRunning}
                              aria-label="Select all visible users"
                            />
                          </th>
                        )}
                        <th>User ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Title</th>
                        <th>Division</th>
                        <th>Status</th>
                        {canWriteJobDivaUsers && <th>Set Division</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredJobDivaUsers.map((u) => (
                        <tr key={u.userId}>
                          {canWriteJobDivaUsers && (
                            <td>
                              <input
                                type="checkbox"
                                checked={jobDivaSelectedUserIds.has(u.userId)}
                                onChange={() => toggleJobDivaUserSelected(u.userId)}
                                disabled={jobDivaBulkRunning}
                                aria-label={`Select ${u.firstName} ${u.lastName}`}
                              />
                            </td>
                          )}
                          <td>{u.userId}</td>
                          <td>{`${u.firstName} ${u.lastName}`.trim() || '—'}</td>
                          <td>{u.email || '—'}</td>
                          <td>{u.title || '—'}</td>
                          <td>{u.divisionName || <em>None</em>}</td>
                          <td>{u.active ? 'Active' : 'Inactive'}</td>
                          {canWriteJobDivaUsers && (
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <select
                                value={jobDivaRowDivisions[u.userId] || ''}
                                onChange={(e) => setJobDivaRowDivisions((prev) => ({ ...prev, [u.userId]: e.target.value }))}
                                disabled={jobDivaUserSavingId === u.userId || jobDivaBulkRunning}
                              >
                                <option value="">—</option>
                                {jobDivaAssignableDivisions.map((name) => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                style={{ marginLeft: 6 }}
                                onClick={() => saveJobDivaUserDivision(u)}
                                disabled={!jobDivaRowDivisions[u.userId] || jobDivaUserSavingId !== null || jobDivaBulkRunning}
                              >
                                {jobDivaUserSavingId === u.userId ? 'Saving...' : 'Save'}
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <ConfirmModal
                show={jobDivaBulkConfirmOpen}
                title="Bulk Update JobDiva Divisions"
                message={`Set the division to "${jobDivaBulkDivision}" for ${jobDivaSelectedUserIds.size} JobDiva user(s)? This writes directly to JobDiva.`}
                onConfirm={runJobDivaBulkDivisionUpdate}
                onCancel={() => setJobDivaBulkConfirmOpen(false)}
                confirmText="Update Divisions"
                confirmStyle="primary"
              />
            </div>
          )}

          {jobDivaSubTab === 'jobs' && (
            <>

          <div className="admin-jobdiva-card">
            <h3>Connection Status</h3>
            {jobDivaStatusLoading ? (
              <div>Loading status...</div>
            ) : jobDivaStatus ? (
              <div className="admin-jobdiva-status-grid">
                <div><strong>Configured:</strong> {jobDivaStatus.isConfigured ? 'Yes' : 'No'}</div>
                <div><strong>Token Cached:</strong> {jobDivaStatus.tokenCached ? 'Yes' : 'No'}</div>
                <div><strong>Jobs Cached:</strong> {jobDivaStatus.jobsCached ? 'Yes' : 'No'}</div>
                <div><strong>Last API Sync:</strong> {jobDivaLastApiSyncAgo}</div>
                <div><strong>Cleanup Enabled:</strong> {jobDivaStatus.descriptionCleanupEnabled ? 'Yes' : 'No'}</div>
                <div><strong>AI Ready:</strong> {jobDivaStatus.descriptionCleanupAiReady ? 'Yes' : 'No'}</div>
                <div><strong>Auth Timeout:</strong> {jobDivaStatus.authTimeoutMs || 'N/A'} ms</div>
                <div><strong>Request Timeout:</strong> {jobDivaStatus.requestTimeoutMs || 'N/A'} ms</div>
                <div><strong>Auth URL:</strong> {jobDivaStatus.authUrl || 'N/A'}</div>
              </div>
            ) : (
              <div>No status available.</div>
            )}

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Scheduled Cache Refresh</h4>
              {jobDivaScheduleLoading ? (
                <div>Loading schedule...</div>
              ) : (
                <>
                  <div className="admin-jobdiva-status-grid" style={{ marginBottom: 8 }}>
                    <div><strong>Interval Minutes:</strong> {jobDivaSchedule?.intervalMinutes ?? 'N/A'}</div>
                    <div><strong>Nightly UTC:</strong> {String(jobDivaSchedule?.nightlyHourUtc ?? 'N/A').padStart(2, '0')}:{String(jobDivaSchedule?.nightlyMinuteUtc ?? 'N/A').padStart(2, '0')}</div>
                    <div><strong>Next Nightly:</strong> {jobDivaSchedule?.nextNightlyRunAt ? new Date(jobDivaSchedule.nextNightlyRunAt).toLocaleString() : 'Not scheduled'}</div>
                    <div><strong>Run On Startup:</strong> {jobDivaSchedule?.runOnStartup ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="admin-jobdiva-filters" style={{ marginBottom: 8 }}>
                    <label>
                      <span>Interval (minutes)</span>
                      <input
                        type="number"
                        min="0"
                        value={jobDivaScheduleForm.intervalMinutes}
                        onChange={(e) => handleJobDivaScheduleFormChange('intervalMinutes', e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Nightly Hour UTC</span>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={jobDivaScheduleForm.nightlyHourUtc}
                        onChange={(e) => handleJobDivaScheduleFormChange('nightlyHourUtc', e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Nightly Minute UTC</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={jobDivaScheduleForm.nightlyMinuteUtc}
                        onChange={(e) => handleJobDivaScheduleFormChange('nightlyMinuteUtc', e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Run On Startup</span>
                      <input
                        type="checkbox"
                        checked={jobDivaScheduleForm.runOnStartup}
                        onChange={(e) => handleJobDivaScheduleFormChange('runOnStartup', e.target.checked)}
                      />
                    </label>
                  </div>
                  <div className="admin-jobdiva-table-toolbar">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={saveJobDivaSchedule} disabled={jobDivaScheduleSaving}>
                      {jobDivaScheduleSaving ? 'Saving...' : 'Save Schedule'}
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={runJobDivaRefreshNow} disabled={jobDivaTestLoading || jobDivaScheduleSaving}>
                      {jobDivaTestLoading ? 'Refreshing...' : 'Refresh Cache Now'}
                    </button>
                  </div>
                  {jobDivaScheduleError && <div style={{ color: 'red', marginTop: 8 }}>{jobDivaScheduleError}</div>}
                  {jobDivaScheduleSuccess && <div style={{ color: '#166534', marginTop: 8 }}>{jobDivaScheduleSuccess}</div>}
                </>
              )}
            </div>
          </div>

          <div className="admin-jobdiva-card">
            <div className="admin-jobdiva-review-header">
              <div>
                <h3>Flagged Description Reviews {jobDivaFlaggedItems.length > 0 && <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#666' }}>({jobDivaFlaggedItems.length})</span>}</h3>
                <p className="admin-jobdiva-subtle">
                  Review AI-cleaned descriptions that were held for approval because confidence was low or quality flags were raised.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={fetchJobDivaFlaggedItems}
                disabled={jobDivaFlaggedLoading || jobDivaReviewActionId !== null}
              >
                {jobDivaFlaggedLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {jobDivaFlaggedError && <div className="admin-jobdiva-inline-error">{jobDivaFlaggedError}</div>}

            {jobDivaFlaggedLoading ? (
              <div>Loading flagged items...</div>
            ) : jobDivaFlaggedItems.length === 0 ? (
              <div>No flagged JobDiva description cleanups are waiting for review.</div>
            ) : (
              <div className="admin-jobdiva-review-list">
                {jobDivaFlaggedItems.map((item) => {
                  const reviewNote = jobDivaReviewNotes[item.cleanupId] || '';
                  const isReviewing = jobDivaReviewActionId === item.cleanupId;
                  const isExpanded = jobDivaFlaggedExpanded.has(item.cleanupId);
                  const toggleExpanded = () => setJobDivaFlaggedExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(item.cleanupId)) { next.delete(item.cleanupId); } else { next.add(item.cleanupId); }
                    return next;
                  });

                  return (
                    <div key={item.cleanupId} className="admin-jobdiva-review-card">
                      <button
                        type="button"
                        className="admin-jobdiva-review-summary"
                        onClick={toggleExpanded}
                        aria-expanded={isExpanded}
                      >
                        <span className="admin-jobdiva-review-summary-toggle">{isExpanded ? '▼' : '▶'}</span>
                        <span className="admin-jobdiva-review-summary-id">Job {item.jobExternalId || '—'}</span>
                        <span className="admin-jobdiva-review-summary-confidence">{item.confidence == null ? '—' : `${Math.round(Number(item.confidence) * 100)}% confidence`}</span>
                        <span className="admin-jobdiva-review-badge">{item.approvalStatus || 'pending_review'}</span>
                        {Array.isArray(item.qualityFlags) && item.qualityFlags.length > 0 && (
                          <span className="admin-jobdiva-review-summary-flags">
                            {item.qualityFlags.map((flag) => (
                              <span key={`${item.cleanupId}-${flag}`} className="admin-jobdiva-flag-chip">{flag}</span>
                            ))}
                          </span>
                        )}
                      </button>

                      {isExpanded && (
                        <div className="admin-jobdiva-review-detail">
                          <div className="admin-jobdiva-review-meta">
                            <div><strong>Job ID:</strong> {item.jobExternalId || '—'}</div>
                            <div><strong>Confidence:</strong> {item.confidence == null ? '—' : `${Math.round(Number(item.confidence) * 100)}%`}</div>
                            <div><strong>Status:</strong> <span className="admin-jobdiva-review-badge">{item.approvalStatus || 'pending_review'}</span></div>
                            <div><strong>Updated:</strong> {formatJobDivaTimestamp(item.updatedAt)}</div>
                          </div>

                          <div className="admin-jobdiva-review-columns">
                            <div className="admin-jobdiva-review-pane">
                              <h4>Original Description</h4>
                              <div className="admin-jobdiva-review-text">{item.sourceDescription || 'No source description stored.'}</div>
                            </div>
                            <div className="admin-jobdiva-review-pane">
                              <h4>Cleaned Description</h4>
                              <div
                                className="admin-jobdiva-review-html"
                                dangerouslySetInnerHTML={{ __html: item.cleanedHtml || '<p>No cleaned HTML stored.</p>' }}
                              />
                            </div>
                          </div>

                          <label className="admin-jobdiva-review-note">
                            <span>Review note</span>
                            <textarea
                              value={reviewNote}
                              onChange={(e) => handleJobDivaReviewNoteChange(item.cleanupId, e.target.value)}
                              placeholder="Optional note about why this was approved or rejected"
                              rows={3}
                            />
                          </label>

                          <div className="admin-jobdiva-review-actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => handleJobDivaCleanupReview(item.cleanupId, 'approved')}
                              disabled={isReviewing}
                            >
                              {isReviewing ? 'Saving...' : 'Approve'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleJobDivaCleanupReview(item.cleanupId, 'rejected')}
                              disabled={isReviewing}
                            >
                              {isReviewing ? 'Saving...' : 'Reject'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="admin-jobdiva-card">
            <h3>JobDiva Actions</h3>
            <div className="admin-jobdiva-table-toolbar" style={{ marginBottom: 12 }}>
              <button type="button" onClick={runJobDivaCallApi} disabled={jobDivaTestLoading || jobDivaCleanupLoading || jobDivaResetLoading}>
                {jobDivaTestLoading ? 'Refreshing...' : 'Refresh from JobDiva API'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={applyJobDivaFilters}
                disabled={jobDivaTestLoading || jobDivaCleanupLoading || jobDivaResetLoading}
              >
                {jobDivaTestLoading ? 'Applying...' : 'Apply Filters'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={runJobDivaCleanup}
                disabled={jobDivaCleanupLoading || jobDivaTestLoading || jobDivaResetLoading}
              >
                {jobDivaCleanupLoading ? 'Cleaning...' : 'Clean Job Descriptions'}
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={resetJobDivaCleanups}
                disabled={jobDivaResetLoading || jobDivaCleanupLoading || jobDivaTestLoading}
              >
                {jobDivaResetLoading ? 'Resetting...' : 'Reset Cleanup Table'}
              </button>
            </div>
            <div className="admin-jobdiva-filters">
              <label>
                <span>Use Filter Criteria</span>
                <input
                  type="checkbox"
                  checked={jobDivaFiltersEnabled}
                  onChange={(e) => setJobDivaFiltersEnabled(e.target.checked)}
                />
              </label>
              <label>
                <span>JobDiva #</span>
                <input
                  type="text"
                  value={jobDivaForm.reference}
                  onChange={(e) => handleJobDivaFormChange('reference', e.target.value)}
                  disabled={!jobDivaFiltersEnabled}
                />
              </label>
              <label>
                <span>Keyword</span>
                <input
                  type="text"
                  value={jobDivaForm.keyword}
                  onChange={(e) => handleJobDivaFormChange('keyword', e.target.value)}
                  disabled={!jobDivaFiltersEnabled}
                />
              </label>
              <label>
                <span>Company</span>
                <input
                  type="text"
                  value={jobDivaForm.company}
                  onChange={(e) => handleJobDivaFormChange('company', e.target.value)}
                  disabled={!jobDivaFiltersEnabled}
                />
              </label>
              <label>
                <span>Division</span>
                <select
                  value={jobDivaForm.division}
                  onChange={(e) => handleJobDivaFormChange('division', e.target.value)}
                  disabled={!jobDivaFiltersEnabled}
                >
                  <option value="">Any</option>
                  <option value="RSC Solutions">RSC Solutions</option>
                  <option value="RSC Healthcare">RSC Healthcare</option>
                </select>
              </label>
              <label>
                <span>Priority</span>
                <input
                  type="text"
                  value={jobDivaForm.priority}
                  onChange={(e) => handleJobDivaFormChange('priority', e.target.value)}
                  disabled={!jobDivaFiltersEnabled}
                />
              </label>
              <label>
                <span>Post to Portal</span>
                <select
                  value={jobDivaForm.posttoportal}
                  onChange={(e) => handleJobDivaFormChange('posttoportal', e.target.value)}
                  disabled={!jobDivaFiltersEnabled}
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </label>
              <label>
                <span>Desc Status</span>
                <select
                  value={jobDivaForm.descStatus}
                  onChange={(e) => handleJobDivaFormChange('descStatus', e.target.value)}
                  disabled={!jobDivaFiltersEnabled}
                >
                  <option value="">Any</option>
                  <option value="none">None</option>
                  <option value="pending_sync">Pending Sync</option>
                  <option value="skipped">Skipped</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="cleaned">Cleaned</option>
                  <option value="approved">Approved</option>
                  <option value="auto_approved">Auto Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </label>
            </div>

            {jobDivaFiltersAppliedAt && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
                Filters applied at {jobDivaFiltersAppliedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            )}

            {jobDivaError && <div style={{ color: 'red', marginTop: 8 }}>{jobDivaError}</div>}

            {(jobDivaCleanupSummary || jobDivaCleanupProgress?.active || jobDivaCleanupLoading) && (
              <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                <div className="admin-jobdiva-result-status ok">
                  Cleanup Summary {jobDivaCleanupProgress?.active ? '(Live)' : '(Latest)'}
                </div>
                <div style={{ margin: '8px 0 10px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <strong>Progress</strong>
                    <span>{jobDivaCleanupPercent}%</span>
                  </div>
                  <div
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={jobDivaCleanupPercent}
                    style={{
                      width: '100%',
                      height: 10,
                      backgroundColor: '#e5e7eb',
                      borderRadius: 999,
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        width: `${jobDivaCleanupPercent}%`,
                        height: '100%',
                        backgroundColor: '#2563eb',
                        transition: 'width 300ms ease'
                      }}
                    />
                  </div>
                </div>
                <div><strong>Reason:</strong> {jobDivaCleanupProgress?.reason || jobDivaCleanupSummary?.reason || 'N/A'}</div>
                <div><strong>Attempted:</strong> {jobDivaCleanupProgress?.attempted ?? jobDivaCleanupSummary?.attempted ?? 0}</div>
                <div><strong>Candidates Completed:</strong> {jobDivaCleanupProgress?.completedCandidates ?? 0} / {jobDivaCleanupProgress?.totalCandidates ?? 0}</div>
                <div><strong>Processed:</strong> {jobDivaCleanupProgress?.processed ?? jobDivaCleanupSummary?.processed ?? 0}</div>
                <div><strong>Skipped:</strong> {jobDivaCleanupProgress?.skipped ?? jobDivaCleanupSummary?.skipped ?? 0}</div>
                <div><strong>Failed:</strong> {jobDivaCleanupProgress?.failed ?? jobDivaCleanupSummary?.failed ?? 0}</div>
                <div><strong>Fallback Used:</strong> {jobDivaCleanupProgress?.fallbackUsed ?? jobDivaCleanupSummary?.fallbackUsed ?? 0}</div>
                <div><strong>Last Job Processed:</strong> {jobDivaCleanupProgress?.lastJobExternalId || 'N/A'}</div>
                <div><strong>Updated:</strong> {jobDivaCleanupProgress?.updatedAt ? new Date(jobDivaCleanupProgress.updatedAt).toLocaleString() : 'N/A'}</div>
              </div>
            )}

            {jobDivaResult && (
              <div className="admin-jobdiva-result">
                <div className={`admin-jobdiva-result-status ${jobDivaResult.ok ? 'ok' : 'error'}`}>
                  {jobDivaResult.ok ? 'Connected' : 'Connection Failed'}
                  {' '}
                  {jobDivaResult.responseTimeMs ? `(${jobDivaResult.responseTimeMs} ms)` : ''}
                </div>

                <div>
                  <strong>JobDiva API URL:</strong> {jobDivaStatus?.openJobsUrl || 'N/A'}
                </div>

                <div>
                  <strong>Return Code:</strong> {jobDivaResult.returnStatusCode || 'N/A'}
                </div>

                {jobDivaResult.error && (
                  <div className="admin-jobdiva-error-box">
                    <div><strong>Error Code:</strong> {jobDivaResult.error.code || 'N/A'}</div>
                    <div><strong>Message:</strong> {jobDivaResult.error.message || 'N/A'}</div>
                    <div><strong>Retryable:</strong> {jobDivaResult.error.retryable ? 'Yes' : 'No'}</div>
                    {jobDivaResult.error.details && (
                      <div><strong>Details:</strong> {String(jobDivaResult.error.details)}</div>
                    )}
                  </div>
                )}

                {jobDivaResult.data && (
                  <>
                    {jobDivaPreviewRows.length > 0 && (
                      <div>
                        <div className="admin-jobdiva-table-toolbar">
                          <div><strong>Total Records:</strong> {jobDivaMeta?.total ?? jobDivaPreviewRows.length}</div>
                          <div><strong>Page:</strong> {jobDivaMeta?.page ?? jobDivaPage} of {jobDivaMeta?.totalPages ?? 1}</div>
                          <label htmlFor="jobdiva-preview-limit"><strong>Rows per page:</strong></label>
                          <select
                            id="jobdiva-preview-limit"
                            value={jobDivaMeta?.pageSize ?? jobDivaPageSize}
                            onChange={(e) => {
                              handleJobDivaPageSizeChange(e.target.value).catch(() => {
                                setJobDivaError('Unexpected pagination error while changing page size.');
                              });
                            }}
                            disabled={jobDivaTestLoading || jobDivaCleanupLoading || jobDivaResetLoading}
                          >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              handleJobDivaPageChange((jobDivaMeta?.page || jobDivaPage) - 1).catch(() => {
                                setJobDivaError('Unexpected pagination error while loading previous page.');
                              });
                            }}
                            disabled={jobDivaTestLoading || jobDivaCleanupLoading || jobDivaResetLoading || (jobDivaMeta?.page || jobDivaPage) <= 1}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              handleJobDivaPageChange((jobDivaMeta?.page || jobDivaPage) + 1).catch(() => {
                                setJobDivaError('Unexpected pagination error while loading next page.');
                              });
                            }}
                            disabled={jobDivaTestLoading || jobDivaCleanupLoading || jobDivaResetLoading || (jobDivaMeta?.page || jobDivaPage) >= (jobDivaMeta?.totalPages || 1)}
                          >
                            Next
                          </button>
                        </div>

                        <div className="admin-jobdiva-table-wrap">
                          <table className="admin-jobdiva-table">
                            <thead>
                              <tr>
                                {jobDivaPreviewColumns.map((column) => (
                                  <th key={column}>{column}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {jobDivaPreviewRows.map((row) => (
                                <tr key={row._rowId}>
                                  {jobDivaPreviewColumns.map((column) => (
                                    <td key={`${row._rowId}-${column}`}>
                                      {column === 'view' ? (
                                        <button
                                          type="button"
                                          className="btn btn-secondary btn-sm"
                                          onClick={() => openJobDivaDescriptionModal(row._job)}
                                        >
                                          View
                                        </button>
                                      ) : column === 'desc status' ? (
                                        <span className={`admin-jobdiva-desc-status admin-jobdiva-desc-status--${row._descStatus}`}>
                                          {row._descStatus === 'cleaned' ? 'Cleaned' : row._descStatus === 'pending_review' ? 'Pending Review' : row._descStatus === 'none' ? 'Original' : row._descStatus}
                                        </span>
                                      ) : (row[column] ?? '—')}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <pre className="admin-jobdiva-json">{JSON.stringify(jobDivaResult.data, null, 2)}</pre>
                  </>
                )}
              </div>
            )}
          </div>
          </>
          )}

          {jobDivaSubTab === 'candidates' && (
            <div className="admin-jobdiva-card">
              <h3>Candidate API Record</h3>
              <p className="admin-jobdiva-subtle">
                Load a candidate by ID to inspect the sanitized response returned by JobDiva.
              </p>
              <form className="admin-jobdiva-filters" onSubmit={loadJobDivaCandidate}>
                <label>
                  <span>Candidate ID</span>
                  <input
                    type="text"
                    value={jobDivaCandidateId}
                    onChange={(e) => setJobDivaCandidateId(e.target.value)}
                    placeholder="e.g. 20301476761901"
                  />
                </label>
                <button type="submit" className="btn btn-primary" disabled={jobDivaCandidateLoading}>
                  {jobDivaCandidateLoading ? 'Loading...' : 'Load Candidate'}
                </button>
              </form>

              {jobDivaCandidateError && <div className="admin-jobdiva-inline-error">{jobDivaCandidateError}</div>}
              {jobDivaCandidateResult && (
                <>
                  <div className="admin-jobdiva-status-grid" style={{ marginTop: 16 }}>
                    <div><strong>Candidate ID:</strong> {jobDivaCandidateResult.candidateId}</div>
                    <div><strong>Endpoints:</strong> {(jobDivaCandidateResult.endpoints || []).join(' | ')}</div>
                  </div>
                  <h4>Raw JobDiva Response</h4>
                  <pre className="admin-jobdiva-json">{JSON.stringify(jobDivaCandidateResult.rawResponse, null, 2)}</pre>
                  <h4>Normalized Candidate</h4>
                  <pre className="admin-jobdiva-json">{JSON.stringify(jobDivaCandidateResult.normalized, null, 2)}</pre>
                </>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === 'zoho-diagnostics' && canRunZohoDiagnostics && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ margin: 0 }}>Zoho Recruit Diagnostics</h2>
            <button onClick={fetchZohoStatus} disabled={zohoStatusLoading || zohoActionLoading}>
              {zohoStatusLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
          </div>

          <div className="admin-jobdiva-card">
            <h3>Connection Status</h3>
            {zohoStatusLoading ? (
              <div>Loading status...</div>
            ) : zohoStatus ? (
              <div className="admin-jobdiva-status-grid">
                <div><strong>Configured:</strong> {zohoStatus.isConfigured ? 'Yes' : 'No'}</div>
                <div><strong>Token Cached:</strong> {zohoStatus.tokenCached ? 'Yes' : 'No'}</div>
                <div><strong>Token Persisted:</strong> {zohoStatus.tokenPersisted ? 'Yes' : 'No'}</div>
                <div><strong>Refresh Token Persisted:</strong> {zohoStatus.refreshTokenPersisted ? 'Yes' : 'No'}</div>
                <div><strong>Jobs Cached:</strong> {zohoStatus.jobsCached ? 'Yes' : 'No'}</div>
                <div><strong>Last API Sync:</strong> {zohoLastApiSyncAgo}</div>
                <div><strong>Request Timeout:</strong> {zohoStatus.requestTimeoutMs || 'N/A'} ms</div>
                <div><strong>Refresh Buffer:</strong> {zohoStatus.tokenRefreshBufferMs || 'N/A'} ms</div>
                <div><strong>Module:</strong> {zohoStatus.moduleApiName || 'N/A'}</div>
                <div><strong>API Base URL:</strong> {zohoStatus.apiBaseUrl || 'N/A'}</div>
                <div><strong>OAuth Server:</strong> {zohoStatus.oauthAccountsServer || 'N/A'}</div>
              </div>
            ) : (
              <div>No status available.</div>
            )}
          </div>

          <div className="admin-jobdiva-card">
            <h3>Zoho Recruit Connection</h3>
            <div style={{ marginBottom: 12 }}>
              {zohoStatus?.hasValidConfig ? (
                <div className="admin-jobdiva-result-status ok">
                  ✓ Connected to Zoho Recruit (Self Client OAuth Active)
                </div>
              ) : (
                <div className="admin-jobdiva-result-status error">
                  Not connected - Configure ZOHO_CLIENT_ID and ZOHO_CLIENT_SECRET in .env
                </div>
              )}
            </div>

            {!zohoStatus?.refreshTokenPersisted && (
              <div className="admin-jobdiva-error-box" style={{ marginBottom: 12 }}>
                <div><strong>Bootstrap required:</strong> refresh token is not stored yet.</div>
                <div style={{ marginTop: 8 }}>
                  Paste a fresh Zoho authorization code, click <strong>Bootstrap Auth Code</strong>, then run <strong>Refresh from Zoho API</strong>.
                </div>
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    value={zohoRedirectUriInput}
                    onChange={(e) => setZohoRedirectUriInput(e.target.value)}
                    placeholder="OAuth redirect URI (must match Zoho app exactly)"
                    style={{ width: '100%' }}
                    disabled={zohoOAuthLoading || zohoActionLoading || zohoMetadataLoading}
                  />
                </div>
                <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#6b7280' }}>
                  Redirect URI must match Zoho app callback exactly (including protocol, host, port, and trailing slash).
                </div>
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    value={zohoAuthorizationCode}
                    onChange={(e) => setZohoAuthorizationCode(e.target.value)}
                    placeholder="Paste fresh Zoho authorization code"
                    style={{ width: '100%' }}
                    disabled={zohoOAuthLoading || zohoActionLoading || zohoMetadataLoading}
                  />
                </div>
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={launchZohoOAuth}
                    disabled={zohoOAuthLoading || zohoActionLoading || zohoMetadataLoading}
                    style={{ marginRight: 8 }}
                  >
                    {zohoOAuthLoading ? 'Opening...' : 'Open Zoho Consent Page'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={bootstrapZohoAuthCode}
                    disabled={zohoOAuthLoading || zohoActionLoading || zohoMetadataLoading}
                  >
                    {zohoOAuthLoading ? 'Bootstrapping...' : 'Bootstrap Auth Code'}
                  </button>
                </div>
                {zohoAuthorizeUrl && (
                  <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#6b7280', wordBreak: 'break-all' }}>
                    Last authorize URL: {zohoAuthorizeUrl}
                  </div>
                )}
              </div>
            )}

            {zohoStatus?.tokenExpiresAt && (
              <div style={{ marginBottom: 12, fontSize: 12, color: '#666' }}>
                Token expires at: {new Date(zohoStatus.tokenExpiresAt).toLocaleString()}
              </div>
            )}

            <div className="admin-jobdiva-table-toolbar" style={{ marginBottom: 12 }}>
              <button
                type="button"
                onClick={runZohoCallApi}
                disabled={zohoActionLoading}
              >
                {zohoActionLoading ? 'Syncing...' : 'Refresh from Zoho API'}
              </button>
            </div>

            {zohoError && (
              <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                <div className="admin-jobdiva-result-status error">Error: {zohoError}</div>
              </div>
            )}
          </div>


          <div className="admin-jobdiva-card">
            <h3>Zoho Actions</h3>
            <div className="admin-jobdiva-table-toolbar" style={{ marginBottom: 12 }}>
              <input
                type="text"
                value={zohoRedirectUriInput}
                onChange={(e) => setZohoRedirectUriInput(e.target.value)}
                placeholder="OAuth redirect URI override"
                style={{ minWidth: '340px', flex: '1 1 340px' }}
                disabled={zohoOAuthLoading || zohoActionLoading || zohoMetadataLoading}
              />
              <button type="button" onClick={runZohoCallApi} disabled={zohoActionLoading || zohoMetadataLoading}>
                {zohoActionLoading ? 'Refreshing...' : 'Refresh from Zoho API'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={launchZohoOAuth}
                disabled={zohoOAuthLoading || zohoActionLoading || zohoMetadataLoading}
              >
                {zohoOAuthLoading ? 'Opening...' : 'Open Zoho Consent Page'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={bootstrapZohoAuthCode}
                disabled={zohoOAuthLoading || zohoActionLoading || zohoMetadataLoading}
              >
                {zohoOAuthLoading ? 'Bootstrapping...' : 'Bootstrap Auth Code'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={applyZohoFilters}
                disabled={zohoActionLoading || zohoMetadataLoading}
              >
                {zohoActionLoading ? 'Applying...' : 'Apply Filters'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={refreshZohoToken}
                disabled={zohoActionLoading || zohoMetadataLoading}
              >
                {zohoActionLoading ? 'Refreshing...' : 'Refresh OAuth Token'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={fetchZohoMetadata}
                disabled={zohoMetadataLoading || zohoActionLoading}
              >
                {zohoMetadataLoading ? 'Loading...' : 'Load Module Metadata'}
              </button>
            </div>

            <div className="admin-jobdiva-filters">
              <label>
                <span>Keyword</span>
                <input
                  type="text"
                  value={zohoForm.keyword}
                  onChange={(e) => handleZohoFormChange('keyword', e.target.value)}
                />
              </label>
              <label>
                <span>Location</span>
                <input
                  type="text"
                  value={zohoForm.location}
                  onChange={(e) => handleZohoFormChange('location', e.target.value)}
                />
              </label>
              <label>
                <span>Department</span>
                <input
                  type="text"
                  value={zohoForm.department}
                  onChange={(e) => handleZohoFormChange('department', e.target.value)}
                />
              </label>
              <label>
                <span>Publish</span>
                <select
                  value={zohoForm.publish}
                  onChange={(e) => handleZohoFormChange('publish', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Published</option>
                  <option value="false">Unpublished</option>
                </select>
              </label>
            </div>

            {zohoFiltersAppliedAt && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
                Filters applied at {zohoFiltersAppliedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            )}

            {zohoError && <div style={{ color: 'red', marginTop: 8 }}>{zohoError}</div>}

            {zohoMetadata && (
              <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                <div className="admin-jobdiva-result-status ok">Module Metadata Loaded</div>
                <pre className="admin-jobdiva-json">{JSON.stringify(zohoMetadata, null, 2)}</pre>
              </div>
            )}

            {zohoResult && (
              <div className="admin-jobdiva-result">
                <div className={`admin-jobdiva-result-status ${zohoResult.ok ? 'ok' : 'error'}`}>
                  {zohoResult.ok ? 'Connected' : 'Connection Failed'}
                  {' '}
                  {zohoResult.responseTimeMs ? `(${zohoResult.responseTimeMs} ms)` : ''}
                </div>

                <div>
                  <strong>Zoho API Base:</strong> {zohoStatus?.apiBaseUrl || 'N/A'}
                </div>

                <div>
                  <strong>Return Code:</strong> {zohoResult.returnStatusCode || 'N/A'}
                </div>

                {zohoResult.error && (
                  <div className="admin-jobdiva-error-box">
                    <div><strong>Error Code:</strong> {zohoResult.error.code || 'N/A'}</div>
                    <div><strong>Message:</strong> {zohoResult.error.message || 'N/A'}</div>
                    <div><strong>Retryable:</strong> {zohoResult.error.retryable ? 'Yes' : 'No'}</div>
                    {zohoResult.error.details && (
                      <div><strong>Details:</strong> {String(zohoResult.error.details)}</div>
                    )}
                  </div>
                )}

                {zohoResult.data && (
                  <>
                    {zohoPreviewRows.length > 0 && (
                      <div>
                        <div className="admin-jobdiva-table-toolbar">
                          <div><strong>Total Records:</strong> {zohoMeta?.total ?? zohoPreviewRows.length}</div>
                          <div><strong>Page:</strong> {zohoMeta?.page ?? zohoPage} of {zohoMeta?.totalPages ?? 1}</div>
                          <label htmlFor="zoho-preview-limit"><strong>Rows per page:</strong></label>
                          <select
                            id="zoho-preview-limit"
                            value={zohoMeta?.pageSize ?? zohoPageSize}
                            onChange={(e) => {
                              handleZohoPageSizeChange(e.target.value).catch(() => {
                                setZohoError('Unexpected pagination error while changing page size.');
                              });
                            }}
                            disabled={zohoActionLoading || zohoMetadataLoading}
                          >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              handleZohoPageChange((zohoMeta?.page || zohoPage) - 1).catch(() => {
                                setZohoError('Unexpected pagination error while loading previous page.');
                              });
                            }}
                            disabled={zohoActionLoading || zohoMetadataLoading || (zohoMeta?.page || zohoPage) <= 1}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              handleZohoPageChange((zohoMeta?.page || zohoPage) + 1).catch(() => {
                                setZohoError('Unexpected pagination error while loading next page.');
                              });
                            }}
                            disabled={zohoActionLoading || zohoMetadataLoading || (zohoMeta?.page || zohoPage) >= (zohoMeta?.totalPages || 1)}
                          >
                            Next
                          </button>
                        </div>

                        <div className="admin-jobdiva-table-wrap">
                          <table className="admin-jobdiva-table">
                            <thead>
                              <tr>
                                {zohoPreviewColumns.map((column) => (
                                  <th key={column}>{column}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {zohoPreviewRows.map((row) => (
                                <tr
                                  key={row._rowId}
                                  onClick={() => openZohoDescriptionModal(row._job)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {zohoPreviewColumns.map((column) => (
                                    <td key={`${row._rowId}-${column}`}>
                                      {row[column] ?? '—'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <pre className="admin-jobdiva-json">{JSON.stringify(zohoResult.data, null, 2)}</pre>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'zoho-jobdiva-etl' && canRunZohoDiagnostics && (
        <ZohoJobDivaEtlMap />
      )}

      {activeTab === 'zoho-maintenance' && canRunZohoDiagnostics && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ margin: 0 }}>SDP Maintenance</h2>
            <button onClick={refreshZohoMaintenanceData} disabled={zohoMaintenanceLoading || zohoMaintenanceActionLoading || zohoMaintenanceHistoryLoading}>
              {zohoMaintenanceLoading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>

          <div className="admin-subtabs">
            <button
              type="button"
              className={`admin-subtab-btn ${zohoMaintenanceSubTab === 'ticket-dashboard' ? 'active' : ''}`}
              onClick={() => setZohoMaintenanceSubTab('ticket-dashboard')}
            >
              Ticket Dashboard
            </button>
            <button
              type="button"
              className={`admin-subtab-btn ${zohoMaintenanceSubTab === 'maintenance-status' ? 'active' : ''}`}
              onClick={() => setZohoMaintenanceSubTab('maintenance-status')}
            >
              Maintenance Status
            </button>
            <button
              type="button"
              className={`admin-subtab-btn ${zohoMaintenanceSubTab === 'schedule-controls' ? 'active' : ''}`}
              onClick={() => setZohoMaintenanceSubTab('schedule-controls')}
            >
              Schedule Controls
            </button>
            <button
              type="button"
              className={`admin-subtab-btn ${zohoMaintenanceSubTab === 'run-history' ? 'active' : ''}`}
              onClick={() => setZohoMaintenanceSubTab('run-history')}
            >
              Run History
            </button>
            <button
              type="button"
              className={`admin-subtab-btn ${zohoMaintenanceSubTab === 'azure-function' ? 'active' : ''}`}
              onClick={() => setZohoMaintenanceSubTab('azure-function')}
            >
              Azure Function
            </button>
          </div>

          {zohoMaintenanceSubTab === 'ticket-dashboard' && (
            <div className="admin-jobdiva-card">
              <h3>SDM Auto-Opened Ticket Dashboard</h3>

              <div className="admin-sdm-ticket-kpis">
                <div className="admin-sdm-ticket-kpi">
                  <div className="label">Open</div>
                  <div className="value">{zohoMaintenanceTicketSummary?.open ?? 0}</div>
                </div>
                <div className="admin-sdm-ticket-kpi">
                  <div className="label">In Progress</div>
                  <div className="value">{zohoMaintenanceTicketSummary?.inProgress ?? 0}</div>
                </div>
                <div className="admin-sdm-ticket-kpi">
                  <div className="label">Closed</div>
                  <div className="value">{zohoMaintenanceTicketSummary?.closed ?? 0}</div>
                </div>
                <div className="admin-sdm-ticket-kpi">
                  <div className="label">Sync Errors</div>
                  <div className="value">{zohoMaintenanceTicketSummary?.errors ?? 0}</div>
                </div>
                <div className="admin-sdm-ticket-kpi">
                  <div className="label">This Month</div>
                  <div className="value">{zohoMaintenanceTicketSummary?.thisMonth ?? 0}</div>
                </div>
                <div className="admin-sdm-ticket-kpi">
                  <div className="label">Total</div>
                  <div className="value">{zohoMaintenanceTicketSummary?.total ?? 0}</div>
                </div>
              </div>

              <div className="admin-jobdiva-table-toolbar">
                <label>
                  <span style={{ marginRight: 8 }}>Status Filter:</span>
                  <select
                    value={zohoMaintenanceTicketStatus}
                    onChange={(e) => setZohoMaintenanceTicketStatus(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="closed">Closed</option>
                    <option value="creation_failed">Creation Failed</option>
                  </select>
                </label>
                <button type="button" onClick={() => fetchZohoMaintenanceTickets()} disabled={zohoMaintenanceActionLoading || zohoMaintenanceLoading}>
                  Refresh Tickets
                </button>
              </div>

              {zohoMaintenanceTickets.length > 0 ? (
                <div className="admin-jobdiva-table-wrap">
                  <table className="admin-jobdiva-table admin-zoho-maintenance-history-table">
                    <thead>
                      <tr>
                        <th>Opened</th>
                        <th>Status</th>
                        <th>Title</th>
                        <th>Run Id</th>
                        <th>Trigger</th>
                        <th>Remote Ticket</th>
                        <th>Last Sync Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zohoMaintenanceTickets.map((ticket) => (
                        <tr key={ticket.TicketId}>
                          <td>{ticket.OpenedAt ? new Date(ticket.OpenedAt).toLocaleString() : '—'}</td>
                          <td>{ticket.TicketStatus || '—'}</td>
                          <td>{ticket.Title || '—'}</td>
                          <td>{ticket.MaintenanceRunId ?? '—'}</td>
                          <td>{ticket.TriggerSource || '—'}</td>
                          <td>
                            {ticket.ExternalTicketUrl ? (
                              <a href={ticket.ExternalTicketUrl} target="_blank" rel="noreferrer">{ticket.ExternalTicketNumber || ticket.ExternalTicketId || 'Open Ticket'}</a>
                            ) : (
                              ticket.ExternalTicketNumber || ticket.ExternalTicketId || '—'
                            )}
                          </td>
                          <td>{ticket.LastSyncError || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ marginTop: 10 }}>No auto-opened SDM tickets found for the selected filter.</div>
              )}
            </div>
          )}

          {zohoMaintenanceSubTab === 'maintenance-status' && (
            <div className="admin-jobdiva-card">
              <h3>Maintenance Status</h3>
              {zohoMaintenanceLoading ? (
                <div>Loading status...</div>
              ) : zohoMaintenanceStatus ? (
                <div className="admin-jobdiva-status-grid">
                  <div><strong>Enabled:</strong> {zohoMaintenanceStatus.enabled ? 'Yes' : 'No'}</div>
                  <div><strong>Paused:</strong> {zohoMaintenanceStatus.isPaused ? 'Yes' : 'No'}</div>
                  <div><strong>Running:</strong> {zohoMaintenanceStatus.isRunning ? 'Yes' : 'No'}</div>
                  <div><strong>Next Run:</strong> {zohoMaintenanceStatus.nextRunAt ? new Date(zohoMaintenanceStatus.nextRunAt).toLocaleString() : 'Not scheduled'}</div>
                  <div><strong>Last Run:</strong> {zohoMaintenanceStatus.lastRunAt ? new Date(zohoMaintenanceStatus.lastRunAt).toLocaleString() : 'Never'}</div>
                  <div><strong>Last Result:</strong> {zohoMaintenanceStatus.lastRunStatus || 'N/A'}</div>
                  <div><strong>Day Of Month:</strong> {zohoMaintenanceStatus.schedule?.runDayOfMonth || 'N/A'}</div>
                  <div><strong>UTC Time:</strong> {String(zohoMaintenanceStatus.schedule?.runHourUtc ?? 'N/A').padStart(2, '0')}:{String(zohoMaintenanceStatus.schedule?.runMinuteUtc ?? 'N/A').padStart(2, '0')}</div>
                  <div><strong>Connected:</strong> {zohoMaintenanceStatus.serviceStatus?.hasValidConfig ? 'Yes' : 'No'}</div>
                  <div><strong>Refresh Token:</strong> {zohoMaintenanceStatus.serviceStatus?.refreshTokenPersisted ? 'Stored' : 'Missing'}</div>
                  <div><strong>Jobs Cached:</strong> {zohoMaintenanceStatus.serviceStatus?.jobsCached ? 'Yes' : 'No'}</div>
                  <div><strong>API Base URL:</strong> {zohoMaintenanceStatus.serviceStatus?.apiBaseUrl || 'N/A'}</div>
                </div>
              ) : (
                <div>No maintenance status available.</div>
              )}
            </div>
          )}

          {zohoMaintenanceSubTab === 'schedule-controls' && (
            <div className="admin-jobdiva-card">
              <h3>Schedule Controls</h3>
              <div className="admin-jobdiva-table-toolbar" style={{ marginBottom: 12 }}>
                <button type="button" onClick={runZohoMaintenanceNow} disabled={zohoMaintenanceActionLoading}>
                  {zohoMaintenanceActionLoading ? 'Running...' : 'Run Now'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={pauseZohoMaintenance} disabled={zohoMaintenanceActionLoading}>
                  Pause
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={resumeZohoMaintenance} disabled={zohoMaintenanceActionLoading}>
                  Resume
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={repairZohoMaintenanceCredentials} disabled={zohoMaintenanceActionLoading}>
                  Repair Credentials
                </button>
              </div>

              <div className="admin-zoho-maintenance-form">
                <label>
                  <span>Enabled</span>
                  <input
                    type="checkbox"
                    checked={zohoMaintenanceForm.enabled}
                    onChange={(e) => handleZohoMaintenanceFormChange('enabled', e.target.checked)}
                  />
                </label>
                <label>
                  <span>Paused</span>
                  <input
                    type="checkbox"
                    checked={zohoMaintenanceForm.isPaused}
                    onChange={(e) => handleZohoMaintenanceFormChange('isPaused', e.target.checked)}
                  />
                </label>
                <label>
                  <span>Day Of Month</span>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={zohoMaintenanceForm.runDayOfMonth}
                    onChange={(e) => handleZohoMaintenanceFormChange('runDayOfMonth', e.target.value)}
                  />
                </label>
                <label>
                  <span>Hour UTC</span>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={zohoMaintenanceForm.runHourUtc}
                    onChange={(e) => handleZohoMaintenanceFormChange('runHourUtc', e.target.value)}
                  />
                </label>
                <label>
                  <span>Minute UTC</span>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={zohoMaintenanceForm.runMinuteUtc}
                    onChange={(e) => handleZohoMaintenanceFormChange('runMinuteUtc', e.target.value)}
                  />
                </label>
              </div>

              <div style={{ marginTop: 12 }}>
                <button type="button" className="btn btn-primary btn-sm" onClick={saveZohoMaintenanceSchedule} disabled={zohoMaintenanceActionLoading}>
                  Save Schedule
                </button>
              </div>

              {zohoMaintenanceSuccess && <div style={{ color: '#107c10', marginTop: 8 }}>{zohoMaintenanceSuccess}</div>}
              {zohoMaintenanceError && <div style={{ color: 'red', marginTop: 8 }}>{zohoMaintenanceError}</div>}
            </div>
          )}

          {zohoMaintenanceSubTab === 'run-history' && (
            <div className="admin-jobdiva-card">
              <h3>Run History</h3>
              {zohoMaintenanceHistoryError && (
                <div role="alert" className="admin-zoho-maintenance-history-error">
                  {zohoMaintenanceHistoryError}
                </div>
              )}
              {zohoMaintenanceHistoryLoading ? (
                <div>Loading history...</div>
              ) : zohoMaintenanceHistory.length > 0 ? (
                <div className="admin-jobdiva-table-wrap">
                  <table className="admin-jobdiva-table admin-zoho-maintenance-history-table">
                    <thead>
                      <tr>
                        <th>Run Id</th>
                        <th>Started</th>
                        <th>Completed</th>
                        <th>Trigger</th>
                        <th>Status</th>
                        <th>Upstream Jobs</th>
                        <th>Changed</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zohoMaintenanceHistory.map((run) => (
                        <tr key={run.RunId}>
                          <td>{run.RunId}</td>
                          <td>{run.StartedAt ? new Date(run.StartedAt).toLocaleString() : '—'}</td>
                          <td>{run.CompletedAt ? new Date(run.CompletedAt).toLocaleString() : '—'}</td>
                          <td>{run.TriggerSource || '—'}</td>
                          <td>{run.Status || '—'}</td>
                          <td>{run.TotalUpstreamJobs ?? '—'}</td>
                          <td>{run.ChangedCount ?? '—'}</td>
                          <td className="admin-zoho-maintenance-run-error">
                            {run.ErrorMessage && <div>{run.ErrorMessage}</div>}
                            {run.ErrorCode && <div><strong>Code:</strong> {run.ErrorCode}</div>}
                            {run.ErrorDetails ? (
                              <details>
                                <summary>Error details for run {run.RunId}</summary>
                                <pre>{typeof run.ErrorDetails === 'string' ? run.ErrorDetails : JSON.stringify(run.ErrorDetails, null, 2)}</pre>
                              </details>
                            ) : run.Status === 'failed' ? (
                              <div>No additional error details were recorded for this run.</div>
                            ) : !run.ErrorMessage && !run.ErrorCode ? '—' : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : !zohoMaintenanceHistoryError && (
                <div>No maintenance runs recorded yet.</div>
              )}
            </div>
          )}
          {zohoMaintenanceSubTab === 'azure-function' && <PolicyReviewAdminTab />}
        </>
      )}

      {activeTab === 'laboredge-diagnostics' && canRunLaborEdgeDiagnostics && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ margin: 0 }}>LaborEdge Diagnostics</h2>
            <button
              onClick={() => {
                fetchLaborEdgeStatus();
                fetchLaborEdgeSchedule();
              }}
              disabled={laborEdgeStatusLoading || laborEdgeActionLoading || laborEdgeScheduleLoading}
            >
              {laborEdgeStatusLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
          </div>

          <div className="admin-jobdiva-card">
            <h3>Connection Status</h3>
            {laborEdgeStatusLoading ? (
              <div>Loading status...</div>
            ) : laborEdgeStatus ? (
              <div className="admin-jobdiva-status-grid">
                <div><strong>Configured:</strong> {laborEdgeStatus.isConfigured ? 'Yes' : 'No'}</div>
                <div><strong>Token Cached:</strong> {laborEdgeStatus.tokenCached ? 'Yes' : 'No'}</div>
                <div><strong>Token Persisted:</strong> {laborEdgeStatus.tokenPersisted ? 'Yes' : 'No'}</div>
                <div><strong>Refresh Token Persisted:</strong> {laborEdgeStatus.refreshTokenPersisted ? 'Yes' : 'No'}</div>
                <div><strong>Jobs Cached:</strong> {laborEdgeStatus.jobsCached ? 'Yes' : 'No'}</div>
                <div><strong>Last API Sync:</strong> {laborEdgeLastApiSyncAgo}</div>
                <div><strong>Cleanup Enabled:</strong> {laborEdgeStatus.descriptionCleanupEnabled ? 'Yes' : 'No'}</div>
                <div><strong>AI Ready:</strong> {laborEdgeStatus.descriptionCleanupAiReady ? 'Yes' : 'No'}</div>
                <div><strong>Request Timeout:</strong> {laborEdgeStatus.requestTimeoutMs || 'N/A'} ms</div>
                <div><strong>Refresh Buffer:</strong> {laborEdgeStatus.tokenRefreshBufferSeconds || 'N/A'} s</div>
                <div><strong>API Base URL:</strong> {laborEdgeStatus.apiBaseUrl || 'N/A'}</div>
                <div><strong>OAuth Token URL:</strong> {laborEdgeStatus.oauthTokenUrl || 'N/A'}</div>
                <div><strong>Organization Code:</strong> {laborEdgeStatus.organizationCode || 'N/A'}</div>
              </div>
            ) : (
              <div>No status available.</div>
            )}

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>Scheduled Cache Refresh</h4>
              {laborEdgeScheduleLoading ? (
                <div>Loading schedule...</div>
              ) : (
                <>
                  <div className="admin-jobdiva-status-grid" style={{ marginBottom: 8 }}>
                    <div><strong>Interval Minutes:</strong> {laborEdgeSchedule?.intervalMinutes ?? 'N/A'}</div>
                    <div><strong>Nightly UTC:</strong> {String(laborEdgeSchedule?.nightlyHourUtc ?? 'N/A').padStart(2, '0')}:{String(laborEdgeSchedule?.nightlyMinuteUtc ?? 'N/A').padStart(2, '0')}</div>
                    <div><strong>Next Nightly:</strong> {laborEdgeSchedule?.nextNightlyRunAt ? new Date(laborEdgeSchedule.nextNightlyRunAt).toLocaleString() : 'Not scheduled'}</div>
                    <div><strong>Run On Startup:</strong> {laborEdgeSchedule?.runOnStartup ? 'Yes' : 'No'}</div>
                  </div>
                  <div className="admin-jobdiva-filters" style={{ marginBottom: 8 }}>
                    <label>
                      <span>Interval (minutes)</span>
                      <input
                        type="number"
                        min="0"
                        value={laborEdgeScheduleForm.intervalMinutes}
                        onChange={(e) => handleLaborEdgeScheduleFormChange('intervalMinutes', e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Nightly Hour UTC</span>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={laborEdgeScheduleForm.nightlyHourUtc}
                        onChange={(e) => handleLaborEdgeScheduleFormChange('nightlyHourUtc', e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Nightly Minute UTC</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={laborEdgeScheduleForm.nightlyMinuteUtc}
                        onChange={(e) => handleLaborEdgeScheduleFormChange('nightlyMinuteUtc', e.target.value)}
                      />
                    </label>
                    <label>
                      <span>Run On Startup</span>
                      <input
                        type="checkbox"
                        checked={laborEdgeScheduleForm.runOnStartup}
                        onChange={(e) => handleLaborEdgeScheduleFormChange('runOnStartup', e.target.checked)}
                      />
                    </label>
                  </div>
                  <div className="admin-jobdiva-table-toolbar">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={saveLaborEdgeSchedule} disabled={laborEdgeScheduleSaving}>
                      {laborEdgeScheduleSaving ? 'Saving...' : 'Save Schedule'}
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={runLaborEdgeRefreshNow} disabled={laborEdgeActionLoading || laborEdgeScheduleSaving}>
                      {laborEdgeActionLoading ? 'Refreshing...' : 'Refresh Cache Now'}
                    </button>
                  </div>
                  {laborEdgeScheduleError && <div style={{ color: 'red', marginTop: 8 }}>{laborEdgeScheduleError}</div>}
                  {laborEdgeScheduleSuccess && <div style={{ color: '#166534', marginTop: 8 }}>{laborEdgeScheduleSuccess}</div>}
                </>
              )}
            </div>
          </div>

          <div className="admin-jobdiva-card">
            <h3>LaborEdge Connection</h3>
            <div style={{ marginBottom: 12 }}>
              {laborEdgeStatus?.hasValidConfig ? (
                <div className="admin-jobdiva-result-status ok">
                  ✓ Connected to LaborEdge
                </div>
              ) : (
                <div className="admin-jobdiva-result-status error">
                  Not connected - configure LABOREDGE credentials in .env
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={bootstrapLaborEdgeAuth}
                disabled={laborEdgeActionLoading}
                style={{ marginRight: 8 }}
              >
                {laborEdgeActionLoading ? 'Bootstrapping...' : 'Bootstrap Auth'}
              </button>
            </div>

            {laborEdgeStatus?.tokenExpiresAt && (
              <div style={{ marginBottom: 12, fontSize: 12, color: '#666' }}>
                Token expires at: {new Date(laborEdgeStatus.tokenExpiresAt).toLocaleString()}
              </div>
            )}

            {laborEdgeError && (
              <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                <div className="admin-jobdiva-result-status error">Error: {laborEdgeError}</div>
              </div>
            )}
          </div>

          <div className="admin-jobdiva-card">
            <div className="admin-jobdiva-review-header">
              <div>
                <h3>Flagged Description Reviews {laborEdgeFlaggedItems.length > 0 && <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#666' }}>({laborEdgeFlaggedItems.length})</span>}</h3>
                <p className="admin-jobdiva-subtle">
                  Review AI-cleaned LaborEdge descriptions that were held for approval because confidence was low or quality flags were raised.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={fetchLaborEdgeFlaggedItems}
                disabled={laborEdgeFlaggedLoading || laborEdgeReviewActionId !== null}
              >
                {laborEdgeFlaggedLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {laborEdgeFlaggedError && <div className="admin-jobdiva-inline-error">{laborEdgeFlaggedError}</div>}

            {laborEdgeFlaggedLoading ? (
              <div>Loading flagged items...</div>
            ) : laborEdgeFlaggedItems.length === 0 ? (
              <div>No flagged LaborEdge description cleanups are waiting for review.</div>
            ) : (
              <div className="admin-jobdiva-review-list">
                {laborEdgeFlaggedItems.map((item) => {
                  const reviewNote = laborEdgeReviewNotes[item.cleanupId] || '';
                  const isReviewing = laborEdgeReviewActionId === item.cleanupId;
                  const isExpanded = laborEdgeFlaggedExpanded.has(item.cleanupId);
                  const toggleExpanded = () => setLaborEdgeFlaggedExpanded((prev) => {
                    const next = new Set(prev);
                    if (next.has(item.cleanupId)) { next.delete(item.cleanupId); } else { next.add(item.cleanupId); }
                    return next;
                  });

                  return (
                    <div key={item.cleanupId} className="admin-jobdiva-review-card">
                      <button
                        type="button"
                        className="admin-jobdiva-review-summary"
                        onClick={toggleExpanded}
                        aria-expanded={isExpanded}
                      >
                        <span className="admin-jobdiva-review-summary-toggle">{isExpanded ? '▼' : '▶'}</span>
                        <span className="admin-jobdiva-review-summary-id">Job {item.jobExternalId || '—'}</span>
                        <span className="admin-jobdiva-review-summary-confidence">{item.confidence == null ? '—' : `${Math.round(Number(item.confidence) * 100)}% confidence`}</span>
                        <span className="admin-jobdiva-review-badge">{item.approvalStatus || 'pending_review'}</span>
                        {Array.isArray(item.qualityFlags) && item.qualityFlags.length > 0 && (
                          <span className="admin-jobdiva-review-summary-flags">
                            {item.qualityFlags.map((flag) => (
                              <span key={`${item.cleanupId}-${flag}`} className="admin-jobdiva-flag-chip">{flag}</span>
                            ))}
                          </span>
                        )}
                      </button>

                      {isExpanded && (
                        <div className="admin-jobdiva-review-detail">
                          <div className="admin-jobdiva-review-meta">
                            <div><strong>Job ID:</strong> {item.jobExternalId || '—'}</div>
                            <div><strong>Confidence:</strong> {item.confidence == null ? '—' : `${Math.round(Number(item.confidence) * 100)}%`}</div>
                            <div><strong>Status:</strong> <span className="admin-jobdiva-review-badge">{item.approvalStatus || 'pending_review'}</span></div>
                            <div><strong>Updated:</strong> {formatJobDivaTimestamp(item.updatedAt)}</div>
                          </div>

                          <div className="admin-jobdiva-review-columns">
                            <div className="admin-jobdiva-review-pane">
                              <h4>Original Description</h4>
                              <div className="admin-jobdiva-review-text">{item.sourceDescription || 'No source description stored.'}</div>
                            </div>
                            <div className="admin-jobdiva-review-pane">
                              <h4>Cleaned Description</h4>
                              <div
                                className="admin-jobdiva-review-html"
                                dangerouslySetInnerHTML={{ __html: item.cleanedHtml || '<p>No cleaned HTML stored.</p>' }}
                              />
                            </div>
                          </div>

                          <label className="admin-jobdiva-review-note">
                            <span>Review note</span>
                            <textarea
                              value={reviewNote}
                              onChange={(e) => handleLaborEdgeReviewNoteChange(item.cleanupId, e.target.value)}
                              placeholder="Optional note about why this was approved or rejected"
                              rows={3}
                            />
                          </label>

                          <div className="admin-jobdiva-review-actions">
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={() => handleLaborEdgeCleanupReview(item.cleanupId, 'approved')}
                              disabled={isReviewing}
                            >
                              {isReviewing ? 'Saving...' : 'Approve'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleLaborEdgeCleanupReview(item.cleanupId, 'rejected')}
                              disabled={isReviewing}
                            >
                              {isReviewing ? 'Saving...' : 'Reject'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="admin-jobdiva-card">
            <h3>LaborEdge Actions</h3>
            <div className="admin-jobdiva-table-toolbar" style={{ marginBottom: 12 }}>
              <button type="button" onClick={runLaborEdgeCallApi} disabled={laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null}>
                {laborEdgeActionLoading ? 'Refreshing...' : 'Refresh from LaborEdge API'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={applyLaborEdgeFilters}
                disabled={laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null}
              >
                {laborEdgeActionLoading ? 'Applying...' : 'Apply Filters'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={refreshLaborEdgeToken}
                disabled={laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null}
              >
                {laborEdgeActionLoading ? 'Refreshing...' : 'Refresh OAuth Token'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={runLaborEdgeCleanup}
                disabled={laborEdgeCleanupLoading || laborEdgeActionLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null}
              >
                {laborEdgeCleanupLoading ? 'Cleaning...' : 'Clean Job Descriptions'}
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={resetLaborEdgeCleanups}
                disabled={laborEdgeResetLoading || laborEdgeCleanupLoading || laborEdgeActionLoading || laborEdgeReviewActionId !== null}
              >
                {laborEdgeResetLoading ? 'Resetting...' : 'Reset Cleanup Table'}
              </button>
            </div>

            <div className="admin-jobdiva-filters">
              <label>
                <span>Use Filter Criteria</span>
                <input
                  type="checkbox"
                  checked={laborEdgeFiltersEnabled}
                  onChange={(e) => setLaborEdgeFiltersEnabled(e.target.checked)}
                />
              </label>
              <label>
                <span>Job ID</span>
                <input
                  type="text"
                  value={laborEdgeForm.jobId}
                  onChange={(e) => handleLaborEdgeFormChange('jobId', e.target.value)}
                  placeholder="e.g. 37719791"
                  disabled={!laborEdgeFiltersEnabled}
                />
              </label>
              <label>
                <span>Job ID Match</span>
                <select
                  value={laborEdgeForm.jobIdMatchMode}
                  onChange={(e) => handleLaborEdgeFormChange('jobIdMatchMode', e.target.value)}
                  disabled={!laborEdgeFiltersEnabled}
                >
                  <option value="contains">Contains</option>
                  <option value="exact">Exact</option>
                </select>
              </label>
              <label>
                <span>Keyword</span>
                <input
                  type="text"
                  value={laborEdgeForm.keyword}
                  onChange={(e) => handleLaborEdgeFormChange('keyword', e.target.value)}
                  disabled={!laborEdgeFiltersEnabled}
                />
              </label>
              <label>
                <span>Desc Status</span>
                <select
                  value={laborEdgeForm.descStatus}
                  onChange={(e) => handleLaborEdgeFormChange('descStatus', e.target.value)}
                  disabled={!laborEdgeFiltersEnabled}
                >
                  <option value="">Any</option>
                  <option value="none">None</option>
                  <option value="pending_sync">Pending Sync</option>
                  <option value="skipped">Skipped</option>
                  <option value="pending_review">Pending Review</option>
                  <option value="cleaned">Cleaned</option>
                  <option value="approved">Approved</option>
                  <option value="auto_approved">Auto Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </label>
              <label>
                <span>Location</span>
                <input
                  type="text"
                  value={laborEdgeForm.location}
                  onChange={(e) => handleLaborEdgeFormChange('location', e.target.value)}
                  disabled={!laborEdgeFiltersEnabled}
                />
              </label>
              <label>
                <span>Department</span>
                <input
                  type="text"
                  value={laborEdgeForm.department}
                  onChange={(e) => handleLaborEdgeFormChange('department', e.target.value)}
                  disabled={!laborEdgeFiltersEnabled}
                />
              </label>
              <label>
                <span>Featured Job</span>
                <select
                  value={laborEdgeForm.featuredJob}
                  onChange={(e) => handleLaborEdgeFormChange('featuredJob', e.target.value)}
                  disabled={!laborEdgeFiltersEnabled}
                >
                  <option value="">All</option>
                  <option value="true">Yes (Featured)</option>
                  <option value="false">No (Not Featured)</option>
                </select>
              </label>
              <label>
                <span>Display On External Job Board</span>
                <select
                  value={laborEdgeForm.publish}
                  onChange={(e) => handleLaborEdgeFormChange('publish', e.target.value)}
                  disabled={!laborEdgeFiltersEnabled}
                >
                  <option value="">All</option>
                  <option value="true">Yes (Published)</option>
                  <option value="false">No (Unpublished)</option>
                </select>
              </label>
            </div>

            {laborEdgeFiltersAppliedAt && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
                Filters applied at {laborEdgeFiltersAppliedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            )}

            {laborEdgeResult && (
              <>
                {(laborEdgeCleanupSummary || laborEdgeCleanupProgress?.active || laborEdgeCleanupLoading) && (
                  <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                    <div className="admin-jobdiva-result-status ok">
                      Cleanup Summary {laborEdgeCleanupProgress?.active ? '(Live)' : '(Latest)'}
                    </div>
                    <div style={{ margin: '8px 0 10px 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <strong>Progress</strong>
                        <span>{laborEdgeCleanupPercent}%</span>
                      </div>
                      <div
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={laborEdgeCleanupPercent}
                        style={{
                          width: '100%',
                          height: 10,
                          backgroundColor: '#e5e7eb',
                          borderRadius: 999,
                          overflow: 'hidden'
                        }}
                      >
                        <div
                          style={{
                            width: `${laborEdgeCleanupPercent}%`,
                            height: '100%',
                            backgroundColor: '#2563eb',
                            transition: 'width 300ms ease'
                          }}
                        />
                      </div>
                    </div>
                    <div><strong>Reason:</strong> {laborEdgeCleanupProgress?.reason || laborEdgeCleanupSummary?.reason || 'N/A'}</div>
                    <div><strong>Attempted:</strong> {laborEdgeCleanupProgress?.attempted ?? laborEdgeCleanupSummary?.attempted ?? 0}</div>
                    <div><strong>Candidates Completed:</strong> {laborEdgeCleanupProgress?.completedCandidates ?? 0} / {laborEdgeCleanupProgress?.totalCandidates ?? 0}</div>
                    <div><strong>Processed:</strong> {laborEdgeCleanupProgress?.processed ?? laborEdgeCleanupSummary?.processed ?? 0}</div>
                    <div><strong>Skipped:</strong> {laborEdgeCleanupProgress?.skipped ?? laborEdgeCleanupSummary?.skipped ?? 0}</div>
                    <div><strong>Failed:</strong> {laborEdgeCleanupProgress?.failed ?? laborEdgeCleanupSummary?.failed ?? 0}</div>
                    <div><strong>Fallback Used:</strong> {laborEdgeCleanupProgress?.fallbackUsed ?? laborEdgeCleanupSummary?.fallbackUsed ?? 0}</div>
                    <div><strong>Last Job Processed:</strong> {laborEdgeCleanupProgress?.lastJobExternalId || 'N/A'}</div>
                    <div><strong>Updated:</strong> {laborEdgeCleanupProgress?.updatedAt ? new Date(laborEdgeCleanupProgress.updatedAt).toLocaleString() : 'N/A'}</div>
                  </div>
                )}

              <div className="admin-jobdiva-result">
                <div className={`admin-jobdiva-result-status ${laborEdgeResult.ok ? 'ok' : 'error'}`}>
                  {laborEdgeResult.ok ? 'Connected' : 'Connection Failed'}
                  {' '}
                  {laborEdgeResult.responseTimeMs ? `(${laborEdgeResult.responseTimeMs} ms)` : ''}
                </div>

                <div>
                  <strong>LaborEdge API Base:</strong> {laborEdgeStatus?.apiBaseUrl || 'N/A'}
                </div>

                <div>
                  <strong>Return Code:</strong> {laborEdgeResult.returnStatusCode || 'N/A'}
                </div>

                {laborEdgeResult.error && (
                  <div className="admin-jobdiva-error-box">
                    <div><strong>Error Code:</strong> {laborEdgeResult.error.code || 'N/A'}</div>
                    <div><strong>Message:</strong> {laborEdgeResult.error.message || 'N/A'}</div>
                    <div><strong>Retryable:</strong> {laborEdgeResult.error.retryable ? 'Yes' : 'No'}</div>
                    {laborEdgeResult.error.details && (
                      <div><strong>Details:</strong> {String(laborEdgeResult.error.details)}</div>
                    )}
                  </div>
                )}

                {laborEdgeResult.data && (
                  <>
                    {laborEdgePreviewRows.length > 0 && (
                      <div>
                        <div className="admin-jobdiva-table-toolbar">
                          <div><strong>Total Records:</strong> {laborEdgeMeta?.total ?? laborEdgePreviewRows.length}</div>
                          <div><strong>Page:</strong> {laborEdgeMeta?.page ?? laborEdgePage} of {laborEdgeMeta?.totalPages ?? 1}</div>
                          <label htmlFor="laboredge-preview-limit"><strong>Rows per page:</strong></label>
                          <select
                            id="laboredge-preview-limit"
                            value={laborEdgeMeta?.pageSize ?? laborEdgePageSize}
                            onChange={(e) => {
                              handleLaborEdgePageSizeChange(e.target.value).catch(() => {
                                setLaborEdgeError('Unexpected pagination error while changing page size.');
                              });
                            }}
                            disabled={laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null}
                          >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              handleLaborEdgePageChange((laborEdgeMeta?.page || laborEdgePage) - 1).catch(() => {
                                setLaborEdgeError('Unexpected pagination error while loading previous page.');
                              });
                            }}
                            disabled={laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null || (laborEdgeMeta?.page || laborEdgePage) <= 1}
                          >
                            Previous
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              handleLaborEdgePageChange((laborEdgeMeta?.page || laborEdgePage) + 1).catch(() => {
                                setLaborEdgeError('Unexpected pagination error while loading next page.');
                              });
                            }}
                            disabled={laborEdgeActionLoading || laborEdgeCleanupLoading || laborEdgeResetLoading || laborEdgeReviewActionId !== null || (laborEdgeMeta?.page || laborEdgePage) >= (laborEdgeMeta?.totalPages || 1)}
                          >
                            Next
                          </button>
                        </div>

                        <div className="admin-jobdiva-table-wrap">
                          <table className="admin-jobdiva-table">
                            <thead>
                              <tr>
                                {laborEdgePreviewColumns.map((column) => (
                                  <th key={column}>{column}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {laborEdgePreviewRows.map((row) => (
                                <tr
                                  key={row._rowId}
                                  onClick={() => openLaborEdgeDescriptionModal(row._job)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  {laborEdgePreviewColumns.map((column) => (
                                    <td key={`${row._rowId}-${column}`}>
                                      {row[column] ?? '—'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <pre className="admin-jobdiva-json">{JSON.stringify(laborEdgeResult.data, null, 2)}</pre>
                  </>
                )}
              </div>
              </>
            )}
          </div>
        </>
      )}

      {activeTab === 'indeed-rss-diagnostics' && canRunIndeedRssDiagnostics && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h2 style={{ margin: 0 }}>Indeed RSS Diagnostics</h2>
            <button onClick={fetchIndeedRssStatus} disabled={indeedRssStatusLoading || indeedRssActionLoading}>
              {indeedRssStatusLoading ? 'Refreshing...' : 'Refresh Status'}
            </button>
          </div>

          <div className="admin-jobdiva-card">
            <h3>Connection Status</h3>
            {indeedRssStatusLoading ? (
              <div>Loading status...</div>
            ) : indeedRssStatus ? (
              <div className="admin-jobdiva-status-grid">
                <div><strong>Enabled:</strong> {indeedRssStatus.enabled ? 'Yes' : 'No'}</div>
                <div><strong>Last Sync:</strong> {indeedRssLastApiSyncAgo}</div>
                <div><strong>Job Count:</strong> {indeedRssStatus.jobCount || 0}</div>
                <div><strong>RSS URL:</strong> {indeedRssStatus.rssUrl ? <small>{indeedRssStatus.rssUrl.substring(0, 60)}...</small> : 'Not configured'}</div>
                {indeedRssStatus.lastError && (
                  <div style={{ gridColumn: '1 / -1' }}><strong>Last Error:</strong> {indeedRssStatus.lastError.message}</div>
                )}
              </div>
            ) : (
              <div>No status available.</div>
            )}
          </div>

          <div className="admin-jobdiva-card">
            <h3>Indeed RSS Actions</h3>
            <div className="admin-jobdiva-table-toolbar" style={{ marginBottom: 12 }}>
              <button type="button" onClick={runIndeedRssSync} disabled={indeedRssActionLoading}>
                {indeedRssActionLoading ? 'Syncing...' : 'Sync Indeed RSS Feed'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={applyIndeedRssFilters}
                disabled={indeedRssActionLoading}
              >
                {indeedRssActionLoading ? 'Applying...' : 'Apply Filters'}
              </button>
            </div>

            <div className="admin-jobdiva-filters">
              <label>
                <span>Keyword</span>
                <input
                  type="text"
                  value={indeedRssForm.keyword}
                  onChange={(e) => handleIndeedRssFormChange('keyword', e.target.value)}
                  placeholder="Search job titles and descriptions"
                />
              </label>
              <label>
                <span>Location</span>
                <input
                  type="text"
                  value={indeedRssForm.location}
                  onChange={(e) => handleIndeedRssFormChange('location', e.target.value)}
                  placeholder="Search locations"
                />
              </label>
            </div>

            {indeedRssFiltersAppliedAt && (
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4 }}>
                Filters applied at {indeedRssFiltersAppliedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            )}

            {indeedRssError && (
              <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                <div className="admin-jobdiva-result-status error">Error: {indeedRssError}</div>
              </div>
            )}

            {indeedRssResult && (
              <div className="admin-jobdiva-result" style={{ marginTop: 12 }}>
                <div className={`admin-jobdiva-result-status ${indeedRssResult.ok ? 'ok' : 'error'}`}>
                  {indeedRssResult.ok ? `✓ Synced ${indeedRssResult.jobCount || 0} jobs` : `Failed: ${indeedRssResult.error?.message || 'Unknown error'}`}
                </div>
              </div>
            )}

            {indeedRssPreviewRows.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div><strong>Total Records:</strong> {indeedRssPreviewRows.length}</div>
                <div className="admin-jobdiva-table-container" style={{ marginTop: 8, overflowX: 'auto' }}>
                  <table className="admin-jobdiva-table">
                    <thead>
                      <tr>
                        <th style={{ width: '10px' }}>View</th>
                        <th>Title</th>
                        <th>Company</th>
                        <th>Location</th>
                        <th>Posted Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {indeedRssPreviewRows.map((row) => (
                        <tr key={row._rowId} style={{ cursor: 'pointer' }} onClick={() => openIndeedRssDescriptionModal(row._job)}>
                          <td style={{ width: '10px', textAlign: 'center' }}>👁</td>
                          <td>{row.title}</td>
                          <td>{row.company}</td>
                          <td>{row.location}</td>
                          <td>{row['posted date']}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmModal
        show={confirmAction.show}
        title={confirmAction.type === 'clear-queue' ? 'Clear Queue' : 'Stop Email Worker'}
        message={
          confirmAction.type === 'clear-queue'
            ? 'Are you sure you want to clear all pending email queue messages? This action cannot be undone.'
            : 'Are you sure you want to stop the email worker? Processing will pause until it is started again.'
        }
        onConfirm={confirmAction.type === 'clear-queue' ? executeClearQueue : executeKillEmailWorker}
        onCancel={() => setConfirmAction({ show: false, type: null })}
        confirmText={confirmAction.type === 'clear-queue' ? 'Clear Queue' : 'Stop Worker'}
        confirmStyle="danger"
      />

      {selectedJobDivaPreviewJob && (
        <div className="admin-jobdiva-modal-overlay" onClick={closeJobDivaDescriptionModal}>
          <div className="admin-jobdiva-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-jobdiva-modal-header">
              <div>
                <h3>{selectedJobDivaPreviewJob.title || selectedJobDivaPreviewJob.raw?.['job title'] || 'Job Description'}</h3>
                <p className="admin-jobdiva-subtle">
                  {selectedJobDivaPreviewJob.company || selectedJobDivaPreviewJob.raw?.company || 'Unknown company'}
                  {selectedJobDivaPreviewJob.location ? ` • ${selectedJobDivaPreviewJob.location}` : ''}
                </p>
              </div>
              <button type="button" className="close-btn" onClick={closeJobDivaDescriptionModal} aria-label="Close description modal">
                ×
              </button>
            </div>

            <div className="admin-jobdiva-modal-meta">
              <div><strong>Cleanup status:</strong> {selectedJobDivaPreviewJob.descriptionCleanup?.status || 'not_processed'}</div>
              <div><strong>Source:</strong> {selectedJobDivaPreviewJob.descriptionCleanup?.source || 'original'}</div>
              <div><strong>Confidence:</strong> {selectedJobDivaPreviewJob.descriptionCleanup?.confidence == null ? '—' : `${Math.round(Number(selectedJobDivaPreviewJob.descriptionCleanup.confidence) * 100)}%`}</div>
            </div>

            <div className="admin-jobdiva-modal-columns">
              <div className="admin-jobdiva-modal-pane">
                <h4>Original Description</h4>
                <div className="admin-jobdiva-review-text">
                  {getJobDivaSourceDescription(selectedJobDivaPreviewJob) || 'No original description available.'}
                </div>
              </div>

              <div className="admin-jobdiva-modal-pane">
                <h4>Current Served Description</h4>
                {selectedJobDivaPreviewJob.descriptionCleanup?.source === 'ai_cleaned' ? (
                  <div
                    className="admin-jobdiva-review-html"
                    dangerouslySetInnerHTML={{
                      __html: selectedJobDivaPreviewJob.description || '<p>No cleaned description available.</p>'
                    }}
                  />
                ) : (
                  <div className="admin-jobdiva-review-text">
                    {String(selectedJobDivaPreviewJob.description || selectedJobDivaPreviewJob.summary || '').trim() || 'No served description available.'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedZohoPreviewJob && (
        <div className="admin-jobdiva-modal-overlay" onClick={closeZohoDescriptionModal}>
          <div className="admin-jobdiva-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-jobdiva-modal-header">
              <div>
                <h3>{selectedZohoPreviewJob.title || 'Zoho Job Opening'}</h3>
                <p className="admin-jobdiva-subtle">
                  ID: {selectedZohoPreviewJob.id || '—'}
                </p>
              </div>
              <button type="button" className="close-btn" onClick={closeZohoDescriptionModal} aria-label="Close Zoho description modal">
                ×
              </button>
            </div>

            <div className="admin-jobdiva-modal-pane" style={{ marginTop: 8 }}>
              <h4>Job_Description</h4>
              <div className="admin-jobdiva-review-text" style={{ whiteSpace: 'pre-wrap' }}>
                {getZohoJobDescription(selectedZohoPreviewJob) || 'No Job_Description available for this row.'}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedLaborEdgePreviewJob && (
        <div className="admin-jobdiva-modal-overlay" onClick={closeLaborEdgeDescriptionModal}>
          <div className="admin-jobdiva-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-jobdiva-modal-header">
              <div>
                <h3>{selectedLaborEdgePreviewJob.title || 'LaborEdge Job'}</h3>
                <p className="admin-jobdiva-subtle">
                  ID: {selectedLaborEdgePreviewJob.id || '—'}
                </p>
              </div>
              <button type="button" className="close-btn" onClick={closeLaborEdgeDescriptionModal} aria-label="Close LaborEdge description modal">
                ×
              </button>
            </div>

            <div className="admin-jobdiva-modal-pane" style={{ marginTop: 8 }}>
              <h4>Diagnostics</h4>
              <div className="admin-jobdiva-status-grid" style={{ marginBottom: 16 }}>
                <div><strong>Job Type:</strong> {getLaborEdgeRawDiagnosticValue(selectedLaborEdgePreviewJob, ['jobType', 'job type', 'positionType', 'employmentType'])}</div>
                <div><strong>featuredJob:</strong> {getLaborEdgeRawDiagnosticValue(selectedLaborEdgePreviewJob, ['featuredJob', 'featured', 'isFeatured'])}</div>
                <div><strong>displayOnExternalJobBoard:</strong> {getLaborEdgeRawDiagnosticValue(selectedLaborEdgePreviewJob, ['displayOnExternalJobBoard', 'displayonexternaljobboard'])}</div>
                <div><strong>jobStatus:</strong> {getLaborEdgeRawDiagnosticValue(selectedLaborEdgePreviewJob, ['jobStatus', 'jobStatusCode'], selectedLaborEdgePreviewJob.status || '—')}</div>
                <div><strong>hotJob:</strong> {getLaborEdgeRawDiagnosticValue(selectedLaborEdgePreviewJob, ['hotJob', 'hotjob'])}</div>
                <div><strong>Cleanup Status:</strong> {selectedLaborEdgePreviewJob.descriptionCleanup?.status || 'not_processed'}</div>
                <div><strong>Cleanup Source:</strong> {selectedLaborEdgePreviewJob.descriptionCleanup?.source || 'original'}</div>
                <div><strong>Cleanup Confidence:</strong> {selectedLaborEdgePreviewJob.descriptionCleanup?.confidence == null ? '—' : `${Math.round(Number(selectedLaborEdgePreviewJob.descriptionCleanup.confidence) * 100)}%`}</div>
              </div>

              <h4>Description</h4>
              {getLaborEdgeJobDescription(selectedLaborEdgePreviewJob)
                ? (
                  <div
                    className="admin-jobdiva-review-html admin-laboredge-description-preview"
                    dangerouslySetInnerHTML={{
                      __html: formatLaborEdgeDescriptionForPreview(getLaborEdgeJobDescription(selectedLaborEdgePreviewJob))
                    }}
                  />
                )
                : (
                  <div className="admin-jobdiva-review-text">No description available for this row.</div>
                )}
            </div>
          </div>
        </div>
      )}

      {selectedIndeedRssPreviewJob && (
        <div className="admin-jobdiva-modal-overlay" onClick={closeIndeedRssDescriptionModal}>
          <div className="admin-jobdiva-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-jobdiva-modal-header">
              <div>
                <h3>{selectedIndeedRssPreviewJob.title || 'Indeed RSS Job'}</h3>
                <p className="admin-jobdiva-subtle">
                  {selectedIndeedRssPreviewJob.company || 'Unknown company'}
                  {selectedIndeedRssPreviewJob.location ? ` • ${selectedIndeedRssPreviewJob.location}` : ''}
                </p>
              </div>
              <button type="button" className="close-btn" onClick={closeIndeedRssDescriptionModal} aria-label="Close Indeed RSS description modal">
                ×
              </button>
            </div>

            <div className="admin-jobdiva-modal-pane" style={{ marginTop: 8 }}>
              <h4>Description</h4>
              <div className="admin-jobdiva-review-text" style={{ whiteSpace: 'pre-wrap' }}>
                {getIndeedRssJobDescription(selectedIndeedRssPreviewJob) || 'No description available for this job.'}
              </div>
            </div>

            {selectedIndeedRssPreviewJob.url && (
              <div className="admin-jobdiva-modal-pane" style={{ marginTop: 8 }}>
                <h4>Indeed Link</h4>
                <a href={selectedIndeedRssPreviewJob.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                  {selectedIndeedRssPreviewJob.url}
                </a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
